<template>
  <div class="camera-screen" :class="{ 'immersive-mode': isImmersiveMode, 'rbe-cam': isRbeOrderButton && selectedOrder }">
    <HeaderComponent @reload-page="reloadPage" @open-settings="$emit('open-settings')"
      @open-help="$emit('open-help')" @toggle-rider-list="toggleRiderList"
      :waiting-riders-count="waitingRidersCount" :has-rider-activity="hasRiderActivity"
      :riders-alert="hasUnphotoedWaitingRider" :riders-urgent="hasUrgentUnphotoedRider" />

    <div v-if="showRiderList" class="rider-list-overlay" @click.self="showRiderList = false">
      <div class="rider-list-popup">
        <div class="rider-list-popup-header">
          <div class="rider-list-popup-title">{{ $t('message.ridersPopupTitle') }}</div>
          <button class="rider-list-close-btn" @click="showRiderList = false" :aria-label="$t('message.ridersPopupClose')">
            <BzIcon v-if="systemLane" name="close" />
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>

        <!-- Waiting section -->
        <div class="rider-list-section">
          <div class="rider-list-section-header is-waiting">
            <BzIcon v-if="systemLane" name="schedule" />
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
            <span>{{ $t('message.ridersPopupWaiting') }}</span>
            <span class="rider-list-section-count">{{ waitingRiderOrders.length }}</span>
          </div>
          <div v-if="waitingRiderOrders.length === 0" class="rider-list-empty">{{ $t('message.ridersPopupEmptyWaiting') }}</div>
          <div v-else class="rider-list-items">
            <div v-for="order in waitingRiderOrders" :key="order.orderId" class="rider-list-item">
              <div v-if="order.deliveryPlatform" class="rider-list-item-platform">
                <img :src="getPlatformIcon(order.deliveryPlatform)" :alt="order.deliveryPlatform" class="rider-list-item-platform-icon" />
                <span class="rider-list-item-platform-name">{{ platformLabel(order.deliveryPlatform) }}</span>
              </div>
              <div class="rider-list-item-ids">
                <div class="rider-list-item-primary" translate="no">{{ getRiderPrimaryId(order) }}</div>
                <div v-if="getRiderSecondaryId(order)" class="rider-list-item-secondary" translate="no">{{ getRiderSecondaryId(order) }}</div>
              </div>
              <div class="rider-list-item-time is-waiting" :class="`urgency-${riderWaitUrgency(order)}`">
                {{ getRiderWaitText(order) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Delivered section -->
        <div class="rider-list-section">
          <div class="rider-list-section-header is-delivered">
            <BzIcon v-if="systemLane" name="check" />
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
            <span>{{ $t('message.ridersPopupDelivered') }}</span>
            <span class="rider-list-section-count">{{ deliveredRiderList.length }}</span>
          </div>
          <div v-if="deliveredRiderList.length === 0" class="rider-list-empty">{{ $t('message.ridersPopupEmptyDelivered') }}</div>
          <div v-else class="rider-list-items">
            <div v-for="order in deliveredRiderList" :key="order.orderId" class="rider-list-item is-delivered">
              <div v-if="order.deliveryPlatform" class="rider-list-item-platform">
                <img :src="getPlatformIcon(order.deliveryPlatform)" :alt="order.deliveryPlatform" class="rider-list-item-platform-icon" />
                <span class="rider-list-item-platform-name">{{ platformLabel(order.deliveryPlatform) }}</span>
              </div>
              <div class="rider-list-item-ids">
                <div class="rider-list-item-primary" translate="no">{{ getRiderPrimaryId(order) }}</div>
                <div v-if="getRiderSecondaryId(order)" class="rider-list-item-secondary" translate="no">{{ getRiderSecondaryId(order) }}</div>
              </div>
              <div class="rider-list-item-time is-delivered">
                {{ getDeliveredText(order) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RBE pilot (mobile): order code + aggregator icon in a compact bar ABOVE the camera preview,
         so the code is out of the preview and the ticket no longer eclipses it. -->
    <div v-if="isRbeOrderButton && selectedOrder" class="rbe-order-bar">
      <img v-if="selectedOrder.deliveryPlatform && getPlatformIcon(selectedOrder.deliveryPlatform)"
        :src="getPlatformIcon(selectedOrder.deliveryPlatform)" :alt="selectedOrder.deliveryPlatform" class="rbe-order-bar-icon" />
      <span class="rbe-order-bar-code" translate="no">{{ rbeOrderCode(selectedOrder) }}</span>
      <button v-if="isTicketEnabled" class="rbe-ticket-toggle" :class="{ 'rbe-ticket-toggle--open': rbeTicketOpen }"
        @click="rbeTicketOpen = !rbeTicketOpen">
        <BzIcon v-if="systemLane" name="receipt_long" />
        <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-2-2-3 2V5a2 2 0 0 1 2-2zm2 5v2h6V8H9zm0 4v2h6v-2H9z"/></svg>
        {{ rbeTicketOpen ? $t('message.hideTicket') : $t('message.showTicket') }}
      </button>
    </div>
    <CameraPreviewComponent ref="cameraPreview" :selectedOrder="selectedOrder" :settings="settings"
      :selectedCameraId="settings.selectedCameraId || null" :immersiveMode="isImmersiveMode"
      :full-frame="isRbeOrderButton && !!selectedOrder"
      @photo-captured="handlePhotoCaptured"
      @black-image-detected="handleBlackImageDetected"
      @capture-cooldown-active="onCooldownChange"
      @capture-cooldown-running="onCooldownRunning"
      @capture-in-progress="isCapturing = $event"
      @cameras-enumerated="$emit('cameras-enumerated', $event)"
      @stream-info="onStreamInfo"
      @camera-state="$emit('camera-state', $event)"
      @stale-camera-id="$emit('stale-camera-id', $event)" />

    <OrderSelectionComponent v-if="!selectedOrder" :orders="ordersWithPendingPhotos" :restaurant="restaurant" :delivered-rider-order-ids="deliveredRiderOrderIds" @order-selected="selectOrder" @add-manual-order="showManualOrderModal = true" @mark-rider-delivered="$emit('mark-rider-delivered', $event)" />

    <SelectedOrderInfoComponent v-if="selectedOrder" :order="selectedOrder" :photoCount="getPhotoCount(selectedOrder)"
      :restaurant="restaurant" :captureCooldown="captureCooldown" :cooldownRunning="cooldownRunning" :rbe-ticket-open="rbeTicketOpen"
      :photo-and-analyze="photoAndAnalyze" :invite-analyze="inviteAnalyze" :hide-ticket="hideTicketForRetake"
      @clear-order="handleBackToHome" @analyze-order="handleAnalyzeOrder"
      @photo-and-analyze="photoAndAnalyzeOrder"
      @camera-click="triggerPhotoCapture" />

    <!-- Qué esperamos ver en la nueva foto, mientras la hacen.
         Al pulsar "corregir y hacer otra foto" el modal desaparece y con él la lista de lo que
         faltaba: se vuelve a la cámara sabiendo que hay que repetir, pero no de QUÉ. Así que la
         lista se queda aquí, con la misma pinta que tenía en el modal (icono, producto, cantidad),
         hasta que pulsen Analizar — y vuelve con ellos si salen del pedido y lo reabren. -->
    <transition name="hint-fade">
      <div v-if="retakeAimShown" class="retake-aim" data-testid="retake-aim">
        <div class="retake-aim__head">
          <BzIcon v-if="systemLane" name="photo_camera" />
          <i v-else class="pi pi-camera" aria-hidden="true"></i>
          <span>{{ $t('message.retakeAimTitle') }}</span>
        </div>
        <div class="retake-aim__items" :tabindex="systemLane ? 0 : undefined">
          <div v-for="(item, index) in retakeAimVisibleItems" :key="index" class="retake-aim__item" data-testid="retake-aim-item">
            <span class="retake-aim__emoji" v-html="item.icon || retakeAimFallbackIcon"></span>
            <span class="retake-aim__name" translate="no">{{ item.groupName }}</span>
            <span class="retake-aim__qty">{{ $t('message.missingCount', item.missingQuantity, { count: item.missingQuantity }) }}</span>
          </div>
          <div v-if="retakeAimHiddenCount" class="retake-aim__more" data-testid="retake-aim-more">
            {{ $t('message.and') }} {{ $t('message.missingOthers', retakeAimHiddenCount, { count: retakeAimHiddenCount }) }}
          </div>
        </div>
        <div class="retake-aim__foot">{{ $t('message.retakeAimFoot') }}</div>
      </div>
    </transition>

    <!-- Salen del pedido con la re-foto pendiente. No se les impide (antes sí), se les recuerda:
         el aviso se pinta ya sobre la lista de pedidos, que es donde acaban de aterrizar. -->
    <!-- On the design system the three hints over the camera are the toasts' family (Adrián, 13-oct-2026:
         the yellow icon «en mitad de la nada» said nothing): the icon on a disc of its state's colour, the
         words to the left in two levels — what happened, then what to do. No countdown: a hint by the shutter
         is not a notification (14-oct). The reminder names its order. Production's stay as they were. -->
    <transition name="hint-fade">
      <div v-if="showRetakeReminder" class="duplicate-hint duplicate-hint--wrap" :class="{ 'bz-hint bz-hint--wrong': systemLane }"
        data-testid="retake-reminder" role="status">
        <template v-if="systemLane">
          <span class="bz-hint__icon"><BzIcon name="photo_camera" /></span>
          <span class="bz-hint__body">
            <span class="bz-hint__title">{{ retakeReminderCode ? $t('message.retakeReminderTitle', { code: retakeReminderCode }) : $t('message.retakeReminder') }}</span>
            <span v-if="retakeReminderCode" class="bz-hint__text">{{ $t('message.retakeReminderBody') }}</span>
          </span>
        </template>
        <template v-else>
          <i class="pi pi-camera"></i>
          {{ $t('message.retakeReminder') }}
        </template>
      </div>
    </transition>

    <transition name="hint-fade">
      <div v-if="showDuplicateHint" class="duplicate-hint" :class="{ 'bz-hint bz-hint--warn': systemLane }" role="status">
        <template v-if="systemLane">
          <span class="bz-hint__icon"><BzIcon name="warning" /></span>
          <span class="bz-hint__body"><span class="bz-hint__title">{{ $t('message.avoidDuplicatePhotos') }}</span></span>
        </template>
        <template v-else>
          <i class="pi pi-exclamation-triangle"></i>
          {{ $t('message.avoidDuplicatePhotos') }}
        </template>
      </div>
    </transition>

    <transition name="hint-fade">
      <div v-if="showBlackImageHint" class="duplicate-hint duplicate-hint--error" :class="{ 'bz-hint bz-hint--urgent': systemLane }" role="status">
        <template v-if="systemLane">
          <span class="bz-hint__icon"><BzIcon name="hide_image" /></span>
          <span class="bz-hint__body">
            <span class="bz-hint__title">{{ $t('message.blackImageTitle') }}</span>
            <span class="bz-hint__text">{{ $t('message.blackImageBody') }}</span>
          </span>
        </template>
        <template v-else>
          <i class="pi pi-times-circle"></i>
          {{ $t('message.blackImageDetected') }}
        </template>
      </div>
    </transition>

    <transition name="thumb-fade">
      <div
        v-if="lastPhotoUrl && selectedOrder"
        class="last-photo-stack"
        :class="{
          'last-photo-stack--multi': getPhotoCount(selectedOrder) >= 2,
          'last-photo-stack--many': getPhotoCount(selectedOrder) >= 3
        }"
      >
        <div class="last-photo-thumb">
          <img :src="lastPhotoUrl" alt="Last capture" />
        </div>
      </div>
    </transition>

    <AnalysisPopupComponent :analysisResult="analysisResult" :loading="loading" :loadingImages="loadingImages" :show="showAnalysisPopup"
      :error="analysisErrorMessage" :uploadTakingTooLong="uploadTakingTooLong" :analysisTakingTooLong="analysisTakingTooLong" @close="closePopup" @continue-order="continueOrder" @add-photos="addNewPictures" @background-analysis="continueInBackground" />

    <TimeoutPopupComponent :errorMessage="analysisErrorMessage" :title="timeoutPopupTitle" :show="showTimeoutPopup" @close="closeTimeoutPopup" />

    <!-- Between the manual-order modal closing and the answer to "this order already exists": looking
         for it can take a few seconds (socket rebuild, refetch, wide lookup), and a dead screen reads
         as a frozen tablet. The same scrim card as the timeout popup and the same spinner as the
         modal's own "creating" step. -->
    <div v-if="lookingForDuplicate" class="duplicate-lookup" data-testid="manual-duplicate-lookup">
      <div class="duplicate-lookup__card" role="status">
        <BzIcon name="progress_activity" class="duplicate-lookup__icon bz-spinner" />
        <p class="duplicate-lookup__text">{{ $t('message.manualOrderExistsLooking') }}</p>
      </div>
    </div>

    <ManualOrderModal
      :show="showManualOrderModal"
      :restaurantId="restaurant?.id || ''"
      @cancel="showManualOrderModal = false"
      @order-created="handleManualOrderCreated"
    />

    <div v-if="showImmersiveModal" class="immersive-modal-overlay" @click.self="showImmersiveModal = false">
      <div class="immersive-modal">
        <BzIcon v-if="systemLane" name="fullscreen" class="immersive-modal-icon" />
        <i v-else class="pi pi-expand immersive-modal-icon"></i>
        <h3>{{ $t('message.immersiveViewportModalTitle') }}</h3>
        <p>{{ $t('message.immersiveViewportModalBody') }}</p>
        <button data-testid="immersive-modal-dismiss" @click="dismissImmersiveModal">{{ $t('message.immersiveViewportModalButton') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { restPost, parseRestError } from '@/restApiFallback';
import { orderAlreadyExists } from '@/manualDuplicate.js';
import { v4 as uuidv4 } from 'uuid';
import { reportForensicEvent } from '@/forensicEvents';
import HeaderComponent from './HeaderComponent.vue';
import CameraPreviewComponent from './CameraPreviewComponent.vue';
import posthog from 'posthog-js';
import OrderSelectionComponent from './OrderSelectionComponent.vue';
import SelectedOrderInfoComponent from './SelectedOrderInfoComponent.vue';
import AnalysisPopupComponent from './AnalysisPopupComponent.vue';
import TimeoutPopupComponent from './TimeoutPopupComponent.vue';
import ManualOrderModal from './ManualOrderModal.vue';
import BzIcon from '../ui/components/BzIcon.vue';
import { systemLane } from '../ui/lane';
import { platformName } from '@/platformNames';
import {updateAppResponseResults, updateLastAlive, updateOrderAsCorrect} from "@/graphql";
import { getOrderFromIndexedDB, saveOrderToIndexedDB, updateAnalysisInDB, getPhotosByOrderId, getAllPendingPhotos } from '@/utils';
import { enqueueAnalysis, cancelPendingAnalysis } from '@/analysisQueue';
import { analysisResultToast, verdictDecisionPayload } from '@/analysisSummary';
import { isDecidedAnalysis } from '@/verdictState';
import { PHOTO_AND_ANALYZE_ENABLED } from '@/photoAndAnalyze';
import { liveDemoOn, isLiveDemoId } from '@/liveDemo/flag';

import { versionInternal } from '../../package.json';
import Logger from '@/logger';

const uEmojiParser = require('universal-emoji-parser')
const logger = new Logger({ screen: 'CameraScreen' });

// Cuántos productos caben en el panel de "haz la foto a:" sin comerse el visor. Medido sobre los
// veredictos que acaban mostrándolo (Athena, 15-21 sep): 1 producto el 65,9 % de las veces, ≤3 el
// 93,5 % y ≤4 el 97,2 %, con cola hasta 13. Con 4 el panel nunca pasa de 257 px, y el 97 % lo ve
// entero; a partir de 5 se muestran 3 y la cuarta línea dice cuántos quedan. Nada de scroll: el
// panel no recibe toques, así que lo que no se pinta no tendría forma de verse. El carril del sistema
// de diseño no recorta: su panel sí recibe toques y la lista hace scroll (14-oct-2026).
const RETAKE_AIM_MAX_ROWS = 4;
// Mismo respaldo que OrderItem.vue y TicketComponentKDS.vue para un producto sin icono.
const RETAKE_AIM_FALLBACK_ICON = uEmojiParser.parse(':package:');
// How long each hint over the camera stays.
const HINT_MS = { retake: 4000, duplicate: 4000, black: 6000 };

export default {
  name: 'CameraScreen',
  components: {
    HeaderComponent,
    CameraPreviewComponent,
    OrderSelectionComponent,
    SelectedOrderInfoComponent,
    AnalysisPopupComponent,
    TimeoutPopupComponent,
    ManualOrderModal,
    BzIcon,
  },
  props: ['rawOrders', 'settings', 'restaurant', 'availableCameras', 'deliveredRiderOrderIds'],
  inject: ['db', 'isOnline', 'notify', 'markForegroundVerdict', 'unmarkForegroundVerdict', 'setOpenCameraOrder', 'clearOpenCameraOrder', 'handleManualDuplicate'],
  data() {
    return {
      // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
      systemLane: systemLane(),
      localRawOrders: [],
      lookingForDuplicate: false, // a manual order 'already exists' lookup is running
      pendingPhotoKeys: {}, // orderId -> keys of photos still in the local upload queue
      // Keys of photos THIS device captured, kept outside the order objects on purpose.
      // `updateRawOrdersWithLocalData` reassigns localRawOrders wholesale from a base it
      // captured before the write landed, so a key written into an order object can be
      // silently overwritten by a merge that was already in flight. This side-state is
      // owned by the capture path alone and no merge can clobber it.
      locallyCapturedKeys: {}, // orderId -> keys captured on this device this session
      selectedOrder: null,
      loading: false,
      loadingImages: [],
      analysisResult: null,
      showAnalysisPopup: false,
      showTimeoutPopup: false,
      analysisErrorMessage: '',
      timeoutPopupTitle: '',
      uploadTakingTooLong: false,
      analysisTakingTooLong: false,
      healthCheckInterval: null,
      captureCooldown: false,
      // Locked and counting down are NOT the same thing: the shutter locks at the tap (so the
      // sensor is never asked for two photos at once) but the anti-duplicate countdown — and its
      // ring — only starts once a photo exists.
      cooldownRunning: false,
      // Last verdict this tablet has seen per order, as {orderId: isCorrect}. The automatic
      // re-analysis needs to know whether the order is currently WRONG, and every other copy
      // of that fact is unreliable at the moment a photo is saved: `selectedOrder` is a
      // snapshot taken when the order was opened (before any verdict existed),
      // `analysisResult` is cleared by addNewPictures, and the order list only learns the
      // verdict when the realtime event arrives — which is exactly what a deaf socket eats.
      lastVerdictByOrder: {},
      isCapturing: false,
      pendingAnalyze: false,
      // A photo of the open order landed during this visit (since it was opened, or since «corregir y
      // hacer otra foto»). Until one does, the system's lane offers «Foto y analizar» in Analizar's place.
      shotThisVisit: false,
      // That first photo came from the shutter, not from «Foto y analizar»: Analizar invites, once.
      inviteAnalyze: false,
      // Concurrency guard for backgrounded analyses. Once an analysis can run in the
      // background (D), two analyses can be in flight at once (one backgrounded, one in the
      // foreground modal). Each analyzeOrder run takes a unique id; `activeAnalyzeRun` is the
      // run that currently owns the modal, `backgroundedRun` the run the user sent to the
      // background. A resolving run only touches the modal if it is still the active one, and
      // only toasts if it is the backgrounded one — so a late result can never bleed into a
      // different order's modal.
      analyzeSeq: 0,
      activeAnalyzeRun: 0,
      backgroundedRun: 0,
      // Hay una re-foto pendiente en el pedido abierto: pulsaron "corregir y hacer otra foto" y
      // todavía no han analizado. Hasta v4.49.9 esto ADEMÁS cerraba la salida (ver el porqué, y
      // por qué se quitó, en handleBackToHome).
      retakePending: false,
      // Los productos que el análisis marcó como que faltaban, guardados al salir del modal para
      // poder decir en la cámara a qué hay que hacerle la foto.
      retakeItems: [],
      // La misma lista recordada POR PEDIDO, para que el recordatorio vuelva con ellos cuando
      // reabren el pedido. Vive en memoria a propósito: si la tablet se recarga se pierde, igual
      // que `lastVerdictByOrder`, y el pedido sigue en naranja, que es el aviso persistente.
      retakePendingByOrder: {},
      // El template no ve las constantes del módulo.
      retakeAimFallbackIcon: RETAKE_AIM_FALLBACK_ICON,
      // Cuándo se pintó un veredicto con productos que faltan. Es el cronómetro del coste de
      // preguntar: lo que tarda la cocina en decidir con un rider esperando.
      verdictShownAt: null,
      showRetakeReminder: false,
      retakeReminderTimeout: null,
      // The code of the order the reminder is about, taken as the order closes.
      retakeReminderCode: null,
      showDuplicateHint: false,
      duplicateHintTimeout: null,
      showBlackImageHint: false,
      blackImageHintTimeout: null,
      lastCooldownEndTime: null,
      lastPhotoUrl: null,
      lastPhotoKey: null,
      lastPhotoCache: null,
      isTablet: window.innerWidth >= 768,
      isRbeOrderButton: posthog.isFeatureEnabled('rbe-order-button'),
      // Live sales demo on: no PostHog flag to turn the ticket on, and the ticket is what the demo shows.
      isTicketEnabled: liveDemoOn() || posthog.isFeatureEnabled('ticket'),
      rbeTicketOpen: false,
      showImmersiveModal: false,
      showManualOrderModal: false,
      showRiderList: false,
      nowTick: Date.now(),
      streamInfo: null,
    };
  },
  computed: {
    /** El panel de «haz la foto a:» está en pantalla. */
    retakeAimShown() {
      return Boolean(this.selectedOrder && this.retakePending && this.retakeItems.length);
    },
    /**
     * On a phone the panel and the ticket both want the top of the camera, and the panel already lists
     * what matters now (Adrián, 14-oct-2026): the ticket steps aside while it is there. The system's lane.
     */
    hideTicketForRetake() {
      return this.systemLane && !this.isTablet && this.retakeAimShown;
    },
    /**
     * «Foto y analizar» in Analizar's place (the system's lane, Adrián, 14-oct-2026): one tap takes the
     * photo and analyses it. Until the order has a photo of this visit — a new order, or one sent back to
     * the camera to photograph again — and only where Analizar would be (a store with the analysis).
     */
    photoAndAnalyze() {
      if (!PHOTO_AND_ANALYZE_ENABLED) return false;
      if (!this.systemLane || !this.selectedOrder || !this.restaurant?.proActivated) return false;
      if (this.shotThisVisit) return false;
      // A green order is never analysed again: Analizar only confirms it, with no photo to wait for.
      if (this._isOrderLockedCorrect(this.selectedOrder)) return false;
      return this.retakePending || this.getPhotoCount(this.selectedOrder) === 0;
    },
    /** Los productos que caben en el panel de re-foto; el resto se resume en una línea. */
    retakeAimVisibleItems() {
      // On the system's lane the list scrolls, so every product is there (14-oct-2026: with seven, the
      // panel said «y otros 4» and there was no way to see which).
      if (this.systemLane) return this.retakeItems;
      return this.retakeItems.length > RETAKE_AIM_MAX_ROWS
        ? this.retakeItems.slice(0, RETAKE_AIM_MAX_ROWS - 1) // la última línea la ocupa "y otros N"
        : this.retakeItems;
    },
    /** Cuántos productos se quedan fuera del panel (0 = caben todos). */
    retakeAimHiddenCount() {
      return this.retakeItems.length - this.retakeAimVisibleItems.length;
    },
    ordersWithPendingPhotos() {
      // The order-list badges read order.photoCount directly — overlay the queued
      // (not-yet-uploaded) photos so the badge doesn't regress after a full fetch.
      return this.localRawOrders.map((order) => {
        // Same identity rule as getPhotoCount: a queued photo the order already knows
        // about is the same photo, not a second one.
        const known = this.photoKeysOf(order);
        const queued = (this.pendingPhotoKeys[order.orderId] || []).filter(k => !known.has(k)).length;
        const count = Math.max(order.photoCount || 0, known.size) + queued;
        return count !== (order.photoCount || 0) ? { ...order, photoCount: count } : order;
      });
    },
    isImmersiveMode() {
      if (!this.isTablet) return false;
      return this.settings.immersiveViewport !== false;
    },
    waitingRidersCount() {
      return this.waitingRiderOrders.length;
    },
    waitingRiderOrders() {
      if (!this.localRawOrders) return [];
      const delivered = this.deliveredRiderOrderIds || {};
      return this.localRawOrders
        .filter(o =>
          !delivered[o.orderId] &&
          o.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN')
        )
        .sort((a, b) => {
          // Oldest arrival first (they are the most urgent)
          const aTime = a.order?.orderEvents?.find(e => e.event === 'RIDER_CHECK_IN')?.registeredAt;
          const bTime = b.order?.orderEvents?.find(e => e.event === 'RIDER_CHECK_IN')?.registeredAt;
          return new Date(aTime || 0) - new Date(bTime || 0);
        });
    },
    deliveredRiderList() {
      if (!this.localRawOrders) return [];
      const delivered = this.deliveredRiderOrderIds || {};
      return this.localRawOrders
        .filter(o => delivered[o.orderId] && o.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN'))
        .sort((a, b) => (delivered[b.orderId] || 0) - (delivered[a.orderId] || 0));
    },
    hasRiderActivity() {
      // Button stays visible if any rider has been seen during the session (waiting OR already delivered)
      return this.waitingRiderOrders.length > 0 || this.deliveredRiderList.length > 0;
    },
    hasUnphotoedWaitingRider() {
      // Rider is waiting AND the order hasn't been correctly analyzed yet (not green)
      return this.waitingRiderOrders.some(o => !this._isOrderCorrect(o));
    },
    hasUrgentUnphotoedRider() {
      // Same condition + rider has been waiting ≥10 min. Depends on nowTick so it re-evaluates over time.
      const now = this.nowTick;
      return this.waitingRiderOrders.some(o => {
        if (this._isOrderCorrect(o)) return false;
        const ev = o.order?.orderEvents?.find(e => e.event === 'RIDER_CHECK_IN');
        if (!ev?.registeredAt) return false;
        let ts = ev.registeredAt;
        if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) ts += 'Z';
        return now - new Date(ts).getTime() >= 10 * 60 * 1000;
      });
    }
  },
  watch: {
    rawOrders: {
      handler(newOrders) {
        // Preserve manual orders (those not present in server data) when merging
        const serverOrderIds = new Set(newOrders.map(o => o.orderId));
        const manualOrders = this.localRawOrders.filter(
          o => o._isManual && !serverOrderIds.has(o.orderId)
        );
        // Single-assignment merge: hand the freshly-built base to updateRawOrdersWithLocalData so
        // localRawOrders is assigned exactly ONCE (after the IndexedDB merge). Previously we set the
        // server-only shape here and then reassigned the merged shape, causing two full list
        // re-renders per realtime event — the dominant main-thread cost on low-end tablets.
        this.updateRawOrdersWithLocalData([...newOrders, ...manualOrders]);
      },
      immediate: true,
    },
    // La lista que se está pintando AQUÍ es la única versión completa de "lo que hay en la
    // tablet": los pedidos manuales viven solo en este componente hasta que el backend los
    // devuelve, así que App.vue no los tenía y el flujo de ayuda no podía ofrecerlos. Se publica
    // la referencia (no una copia) en cada cambio; el coste es un emit por evento de pedidos.
    ordersWithPendingPhotos: {
      handler(orders) {
        this.$emit('orders-on-screen', orders);
      },
      immediate: true,
    },
    // Arranca el cronómetro en el instante en que se pinta un veredicto CON productos que faltan,
    // no cuando se abre el modal: mientras gira el spinner no hay nada que decidir.
    analysisResult(value) {
      const missing = Array.isArray(value?.missingItems) ? value.missingItems.length : 0;
      this.verdictShownAt = missing > 0 ? Date.now() : null;
    },
    'settings.immersiveViewport': function(newVal, oldVal) {
      // Show the onboarding modal once, the first time immersive becomes active. We must NOT clear
      // the 'seen' flag here: with remote config, a backend `reconfigure` toggling immersive ON
      // would otherwise re-pop the first-run modal on the operator mid-shift.
      if (newVal && !oldVal && this.isImmersiveMode && !localStorage.getItem('immersiveModalSeen')) {
        this.showImmersiveModal = true;
      }
    },
  },
  mounted() {
    this.localRawOrders = [...this.rawOrders];
    // The immersive mode's one-time explanation. The reload button's went on 15-oct-2026 (Adrián): no
    // longer wanted.
    this.$nextTick(() => {
      if (this.isImmersiveMode && !localStorage.getItem('immersiveModalSeen')) {
        this.showImmersiveModal = true;
      }
    });
    logger.console('Component mounted. Initial localRawOrders:', this.localRawOrders);
    this.updateRawOrdersWithLocalData();
    this.seedPendingPhotoKeys();
    window.addEventListener('photoUploaded', this.handlePhotoUploaded);
    // A discarded photo also left the local queue, so the counter still has to recount —
    // it is only the analysis trigger that must not fire for it.
    window.addEventListener('photoDiscarded', this.handlePhotoUploaded);
    window.addEventListener('showVerdictModal', this.handleShowVerdictModal);
    // The health check talks to the backend, so it stays off in demo mode.
    if (!__BRONZE_DEMO__) {
      this.healthCheckInterval = setInterval(this.healthCheck, 600000); // Health check every 10 minutes
      this.healthCheck();
    }
    this._immersiveResizeHandler = () => { this.isTablet = window.innerWidth >= 768; };
    window.addEventListener('resize', this._immersiveResizeHandler);
    // Refresh `nowTick` every 30 s so wait-based reactives (hasUrgentRider) re-evaluate.
    this._nowTickInterval = setInterval(() => { this.nowTick = Date.now(); }, 30000);
  },
  beforeUnmount() {
    this.clearOpenCameraOrder();
    window.removeEventListener('photoUploaded', this.handlePhotoUploaded);
    window.removeEventListener('photoDiscarded', this.handlePhotoUploaded);
    window.removeEventListener('showVerdictModal', this.handleShowVerdictModal);
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    if (this._nowTickInterval) {
      clearInterval(this._nowTickInterval);
      this._nowTickInterval = null;
    }
    if (this.duplicateHintTimeout) {
      clearTimeout(this.duplicateHintTimeout);
      this.duplicateHintTimeout = null;
    }
    if (this.retakeReminderTimeout) {
      clearTimeout(this.retakeReminderTimeout);
      this.retakeReminderTimeout = null;
    }
    if (this.blackImageHintTimeout) {
      clearTimeout(this.blackImageHintTimeout);
      this.blackImageHintTimeout = null;
    }
    if (this.lastPhotoUrl) {
      URL.revokeObjectURL(this.lastPhotoUrl);
      this.lastPhotoUrl = null;
    }
    this.lastPhotoCache = null;
    window.removeEventListener('resize', this._immersiveResizeHandler);
  },
  methods: {
    toggleRiderList() {
      this.showRiderList = !this.showRiderList;
    },
    _isOrderCorrect(o) {
      return o?.order?.manualIsCorrect === true ||
        (o?.order?.orderAnalyses?.isCorrect === true && o?.order?.orderAnalyses?.appResponse === 'ORDER_ANALYZED') ||
        o?.order?.orderAnalyses?.appResponse === 'EMPTY_ORDER_CONTENT';
    },
    _isOrderLockedCorrect(o) {
      // A "locked" correct order can never regress to incorrect, so it must never be
      // re-analyzed (not even with new photos): new photos can only ADD detected items,
      // never remove them, so a correct order stays correct. The ONE exception is
      // EMPTY_ORDER_CONTENT — green only because nothing was detected yet — where a new
      // photo CAN legitimately change the verdict, so it is deliberately NOT locked.
      return o?.order?.manualIsCorrect === true ||
        (o?.order?.orderAnalyses?.isCorrect === true &&
         o?.order?.orderAnalyses?.appResponse === 'ORDER_ANALYZED');
    },
    getRiderPrimaryId(order) {
      return order.deliveryId || order.ticketId || order.posId || order.orderId;
    },
    // The code shown ON THE ORDER TILE (OrderSelectionComponent.getOrderDisplayText):
    // ticketId → deliveryId → posId. Analysis toasts must name the order with THIS same
    // id so the operator matches the toast to the tile they see (the rider-toast primary
    // id is a different, delivery-first priority and must not be reused here).
    orderDisplayLabel(order) {
      return order?.ticketId || order?.deliveryId || order?.posId || order?.orderId;
    },
    getRiderSecondaryId(order) {
      if (order.deliveryId) return order.ticketId || order.posId || null;
      if (order.ticketId) return order.posId || null;
      return null;
    },
    getRiderArrivalMinutes(order) {
      const event = order.order?.orderEvents?.find(e => e.event === 'RIDER_CHECK_IN');
      if (!event?.registeredAt) return null;
      let ts = event.registeredAt;
      if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) {
        ts = ts + 'Z';
      }
      return Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    },
    getRiderWaitText(order) {
      const mins = this.getRiderArrivalMinutes(order);
      if (mins == null) return '';
      if (mins < 1) return this.$t('message.riderJustArrived');
      return this.$t('message.riderWaitMinutes', { minutes: mins });
    },
    riderWaitUrgency(order) {
      const mins = this.getRiderArrivalMinutes(order);
      if (mins == null) return 'none';
      if (mins < 2) return 'low';
      if (mins < 5) return 'medium';
      return 'high';
    },
    getDeliveredText(order) {
      const ts = (this.deliveredRiderOrderIds || {})[order.orderId];
      if (!ts || typeof ts !== 'number') return this.$t('message.ridersPopupDelivered');
      const mins = Math.floor((Date.now() - ts) / 60000);
      const hhmm = new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (mins < 1) return this.$t('message.ridersPopupDeliveredJustNow', { time: hhmm });
      return this.$t('message.ridersPopupDeliveredAt', { time: hhmm, minutes: mins });
    },
    getPlatformIcon(platform) {
      try {
        return require(`@/assets/delivery_platforms/${platform}.png`);
      } catch (e) {
        return require('@/assets/delivery_platforms/OTHERS.png');
      }
    },
    rbeOrderCode(order) {
      // RBE pilot: short code (last 5 of the delivery id — Glovo 12→5, Uber 5→5); fall back to the
      // ticket/pos id when there is no delivery id.
      if (order && order.deliveryId) {
        const id = String(order.deliveryId);
        return id.length > 5 ? id.slice(-5) : id;
      }
      return (order && (order.ticketId || order.posId)) || this.$t('message.unknown');
    },
    platformLabel(platform) {
      return platformName(platform, (key) => this.$t(key));
    },
    reloadPage() {
      window.location.reload();
    },
    dismissImmersiveModal() {
      this.showImmersiveModal = false;
      localStorage.setItem('immersiveModalSeen', '1');
    },
    async handleManualOrderCreated(orderData) {
      try {
        logger.info('Creating manual order', orderData);
        const result = await restPost({
          apiName: 'WEBAPP-API',
          path: '/create-order',
          options: {
            body: {
              delivery_id: orderData.deliveryId,
              delivery_platform: orderData.deliveryPlatform,
              restaurant_id: orderData.restaurantId,
              comment: 'Manual order'
            }
          }
        });
        logger.success('Manual order created', result);

        // Build a local order representation and select it for photo capture
        const orderId = result.order_id || result.orderId || uuidv4();
        const orderAnalysisId = result.order_analysis_id || result.orderAnalysisId || uuidv4();

        const manualOrder = {
          _isManual: true,
          id: result.raw_order_id || result.id || uuidv4(),
          key: orderId,
          orderId: orderId,
          posId: null,
          deliveryId: orderData.deliveryId,
          ticketId: null,
          deliveryPlatform: orderData.deliveryPlatform,
          brand: null,
          brandByBrand: null,
          createdAt: new Date().toISOString(),
          processedAt: null,
          photoCount: 0,
          orderAnalysis: orderAnalysisId,
          analysisResult: null,
          orderContent: null,
          order: {
            hasPhoto: false,
            manualIsCorrect: null,
            photos: [],
            orderAnalyses: {
              id: orderAnalysisId,
              isCorrect: null,
              appResponse: null,
            },
            allergies: null,
            comment: 'Manual order',
            orderDigitalTicket: null,
          }
        };

        // Save to IndexedDB and add to local orders
        const dbInstance = this.db();
        await saveOrderToIndexedDB(dbInstance, manualOrder);
        this.localRawOrders = [...this.localRawOrders, manualOrder];

        // Close modal and select the order for photo capture
        this.showManualOrderModal = false;
        this.selectOrder(manualOrder);
      } catch (error) {
        this.showManualOrderModal = false;
        // "Order with delivery_id X already exists": not a failure to retry, an order to find. The
        // usual cause is a tablet that missed it, so look for it (and repair the tablet) instead of
        // sending the operator round in circles. Old and new 400 bodies are both recognised.
        const duplicate = orderAlreadyExists(parseRestError(error), error?.message);
        if (duplicate) {
          logger.warning('Manual order already exists, looking for it', { duplicate });
          await this.openExistingManualOrder(orderData, duplicate);
          return;
        }
        logger.error('Error creating manual order', { error });
        this.showErrorInTimeoutPopup(this.$t('message.manualOrderError'),
                                     this.$t('message.manualOrderErrorTitle'));
      }
    },
    /**
     * The manual order the backend already had: App finds it (on the tablet, after a socket rebuild,
     * or in a one-off wide lookup), and it is opened here. Nowhere → a specific message, not the
     * generic "try again": retrying can only hit the same 400.
     */
    async openExistingManualOrder(orderData, duplicate) {
      let result = { outcome: 'not_found', order: null, platforms: [] };
      this.lookingForDuplicate = true;
      try {
        result = await this.handleManualDuplicate({
          platform: orderData.deliveryPlatform,
          deliveryId: orderData.deliveryId,
          orderId: duplicate.orderId,
          createdAt: duplicate.createdAt,
          visibleOrders: this.localRawOrders,
        });
      } catch (error) {
        logger.error('Manual duplicate lookup failed', { error });
      } finally {
        this.lookingForDuplicate = false;
      }
      if (result.outcome === 'platform_mismatch') {
        // The backend refused the code, but it is held by ANOTHER platform's order. Opening that
        // one would put this bag's photos on a different order: say which platform holds it.
        const platforms = result.platforms.map((id) => this.platformLabel(id)).join(', ');
        this.showErrorInTimeoutPopup(this.$t('message.manualOrderExistsOtherPlatform', { platform: platforms }),
                                     this.$t('message.manualOrderExistsNotFoundTitle'));
        return;
      }
      if (!result.order) {
        this.showErrorInTimeoutPopup(this.$t('message.manualOrderExistsNotFoundBody'),
                                     this.$t('message.manualOrderExistsNotFoundTitle'));
        return;
      }
      // What is painted here may know more than App does (photos queued locally): prefer it.
      const order = this.localRawOrders.find((o) => o.orderId === result.order.orderId) || result.order;
      this.notify({ text: this.$t('message.manualOrderExistsOpened'), variant: 'info' });
      this.selectOrder(order);
    },
    triggerPhotoCapture() {
      this.isCapturing = true;
      this.$refs.cameraPreview.capturePhoto();
    },
    /**
     * «Foto y analizar»: the shutter's capture, and Analizar pressed while it runs — the path that already
     * analyses as soon as that photo lands (`pendingAnalyze`, handlePhotoCaptured). Nothing happens while
     * the camera is not ready, as with the shutter, so no analysis is left waiting for a photo that will
     * not come.
     */
    photoAndAnalyzeOrder() {
      // The shutter's cooldown too: a capture it swallows would leave the analysis waiting for nothing.
      if (!this.$refs.cameraPreview?.cameraReady || this.captureCooldown) return;
      reportForensicEvent('photo_and_analyze', { order_id: this.selectedOrder?.orderId ?? null });
      this.triggerPhotoCapture();
      this.handleAnalyzeOrder();
    },
    restartCamera() {
      if (this.$refs.cameraPreview) {
        this.$refs.cameraPreview.stopCamera();
        this.$refs.cameraPreview.initCamera();
      }
    },
    onStreamInfo(info) {
      this.streamInfo = info;
      this.$emit('stream-info', info);
    },
    onCooldownChange(active) {
      this.captureCooldown = active;
      if (!active) {
        this.lastCooldownEndTime = Date.now();
      }
    },
    onCooldownRunning(running) {
      this.cooldownRunning = running;
    },
    showDuplicateHintBriefly() {
      if (this.duplicateHintTimeout) {
        clearTimeout(this.duplicateHintTimeout);
      }
      this.showDuplicateHint = true;
      this.duplicateHintTimeout = setTimeout(() => {
        this.showDuplicateHint = false;
        this.duplicateHintTimeout = null;
      }, HINT_MS.duplicate);
    },
    openHelp() {
      this.$emit('open-help');
    },
    async updateRawOrdersWithLocalData(baseOrders = this.localRawOrders) {
      logger.console('Updating localRawOrders with DB order data');
      // Sequence guard: with single-assignment (no synchronous pre-set) overlapping realtime events
      // can run this concurrently. Tag each run and drop stale results so an older in-flight
      // IndexedDB merge cannot clobber a newer one when it resolves out of order.
      const seq = (this._mergeSeq = (this._mergeSeq || 0) + 1);
      const updatedOrders = await Promise.all(baseOrders.map(async (order) => {
        const orderKey = order.orderId;
        const localOrder = await getOrderFromIndexedDB(this.db(), orderKey);

        // Memory optimization: Don't log individual orders
        // logger.console('updateRawOrdersWithLocalData - Order from DB:');
        // logger.console(localOrder)
        // logger.console('updateRawOrdersWithLocalData - Original order from localRawOrders:');
        // logger.console(order)

        if (!localOrder) {
          return order;
        }
        return {
          ...localOrder,
          // Prefer the fresh server value for recipientName / recipientAddress (RBE pilot): the merge
          // makes the IndexedDB copy authoritative, so a stale entry saved before the value was
          // available (older event) would otherwise clobber the one from the latest fetch/realtime event.
          // Address uses || (not ??) so a fresh "" can't override a good local address (empty → name fallback).
          recipientName: order.recipientName ?? localOrder.recipientName,
          recipientAddress: order.recipientAddress || localOrder.recipientAddress,
          _isManual: order._isManual || localOrder._isManual || false,
          allergies: localOrder.allergies || null,
          ticketContent: localOrder.ticketContent || null
        };
      }));
      // Memory optimization: Don't log entire order arrays
      // logger.console('updateRawOrdersWithLocalData - Updated localRawOrders orders:')
      // logger.console(updatedOrders)
      logger.console(`updateRawOrdersWithLocalData - Updated ${updatedOrders.length} orders`)
      if (seq !== this._mergeSeq) return; // a newer merge superseded this one
      this.localRawOrders = updatedOrders;
      this.pruneLocallyCapturedKeys(updatedOrders);
    },
    /**
     * Drop locally captured keys the persisted orders now carry themselves, and keys of
     * orders that are gone. Purely housekeeping — the Set union stays correct either
     * way — but it keeps the map from growing for a whole shift.
     */
    pruneLocallyCapturedKeys(orders) {
      const tracked = Object.keys(this.locallyCapturedKeys);
      if (tracked.length === 0) return;
      const byId = new Map(orders.map(o => [o.orderId, o]));
      const next = {};
      let changed = false;
      for (const orderId of tracked) {
        const order = byId.get(orderId);
        if (!order) { changed = true; continue; } // order no longer on screen
        const persisted = Array.isArray(order.order?.photos) ? order.order.photos : [];
        const stillNeeded = this.locallyCapturedKeys[orderId].filter(k => !persisted.includes(k));
        if (stillNeeded.length !== this.locallyCapturedKeys[orderId].length) changed = true;
        if (stillNeeded.length > 0) next[orderId] = stillNeeded;
      }
      if (changed) this.locallyCapturedKeys = next;
    },
    getPhotoCount(order) {
      // Count DISTINCT photos, never the sum of two counters that overlap.
      //
      // The photo of an order lives in two places whose lifetimes are independent: the
      // order's own `photos` list (a key per photo — the local capture pushes one, and
      // the photo_created event pushes the server's, deduplicated by key), and the local
      // upload queue in IndexedDB. Adding the two double-counted the same photo in the
      // window where the server had already confirmed it but it had not yet left the
      // queue — measured on a tablet as the counter reading 1 -> 2 -> 1 after a SINGLE
      // photo. Worse in the other direction: once a photo left the queue, the count fell
      // back to whatever the server had confirmed, so a photo_created that never arrived
      // (a realtime channel that is down while uploads still work) took the counter to 0
      // — and with it the "Analyse" button, which is `v-if="photoCount > 0"`.
      //
      // Identity fixes both: a photo counts once whether it is confirmed, queued, or both.
      const localOrder = this.localRawOrders.find(o =>
        (order.orderId && o.orderId === order.orderId) ||
        (o.posId === order.posId &&
        o.deliveryId === order.deliveryId &&
        o.ticketId === order.ticketId)
      );
      const pendingKey = localOrder?.orderId || order.orderId;
      const known = this.photoKeysOf(localOrder);
      // A full fetch replaces `photos` with the server's list, so trust whichever is
      // larger: the server's own count, or the keys we can actually name.
      const base = Math.max(localOrder?.photoCount || 0, known.size);
      const queued = this.pendingPhotoKeys[pendingKey] || [];
      const notYetKnown = queued.filter(key => !known.has(key)).length;
      return base + notYetKnown;
    },
    /**
     * The set of photo keys an order is known to have.
     *
     * `order.photos` is deliberately heterogeneous: a local capture pushes the photo key
     * as a string, while the photo_created handler pushes `{ url }` for photos taken on
     * another device. Both normalise to the same key — the leading segment of the S3
     * object name — which is what makes deduplication across the two sources work.
     */
    photoKeysOf(localOrder, orderId = localOrder?.orderId) {
      const keys = new Set();
      const photos = localOrder?.order?.photos;
      if (Array.isArray(photos)) {
        for (const photo of photos) {
          if (typeof photo === 'string') {
            keys.add(photo);
          } else if (photo?.url) {
            keys.add(String(photo.url).split('/').pop().split('_')[0]);
          }
        }
      }
      // Photos captured here that the order object may not know about yet — either because
      // the merge has not run, or because one in flight dropped them.
      for (const key of this.locallyCapturedKeys[orderId] || []) keys.add(key);
      return keys;
    },
    /**
     * Record that this device captured a photo, independently of the order objects.
     *
     * Writing the key into the order itself is not safe: an IndexedDB merge that started
     * before `saveOrderToIndexedDB` committed will reassign localRawOrders from its own
     * stale base and drop the key — reintroducing the very gap this fix closes. Keeping
     * it here means no merge can lose it, and it works even for an order that is not in
     * localRawOrders yet (the first photo of a freshly created manual order).
     * Reassigned rather than mutated so Vue 2 sees the new key.
     */
    rememberLocalPhotoKey(orderId, key) {
      if (!orderId || !key) return;
      const existing = this.locallyCapturedKeys[orderId] || [];
      if (existing.includes(key)) return;
      this.locallyCapturedKeys = { ...this.locallyCapturedKeys, [orderId]: [...existing, key] };
    },
    async refreshPendingPhotoKeys(orderId) {
      if (!orderId) return;
      try {
        const queued = await getPhotosByOrderId(this.db(), orderId);
        // Vue 2 cannot observe a new key added to a plain object; reassign so the
        // counter actually re-renders when the first photo of an order is queued.
        this.pendingPhotoKeys = { ...this.pendingPhotoKeys, [orderId]: queued.map(p => p.key) };
      } catch (error) {
        logger.console('Could not refresh pending photo keys', error);
      }
    },
    async seedPendingPhotoKeys() {
      // After a reload, photos taken before it may still sit in the upload queue.
      try {
        const queued = await getAllPendingPhotos(this.db());
        const keys = {};
        for (const photo of queued) {
          if (!photo.order) continue;
          (keys[photo.order] = keys[photo.order] || []).push(photo.key);
        }
        this.pendingPhotoKeys = keys;
      } catch (error) {
        logger.console('Could not seed pending photo keys', error);
      }
    },
    handlePhotoUploaded() {
      // A queued photo left the local queue: recount (the server-side count catches up
      // through the photo_created realtime event moments later). The displayed count no
      // longer depends on that event arriving — the photo's key stays in the order's
      // photo list either way — so a confirmation that never lands degrades telemetry,
      // not the operator's ability to work.
      this.seedPendingPhotoKeys();
    },
    async continueOrder(payload = null) {
      // La razón del "entregar sin corregir" (piloto de IA wrong). Se manda hacia arriba ANTES de
      // limpiar el pedido: App.vue tiene el messenger y el contexto, y aquí ya no habrá pedido.
      // El brazo de control no da motivo: `reason: null` es un dato, no un hueco.
      this.reportVerdictDecision('deliver', payload || {});
      if (payload?.reason && this.selectedOrder) {
        this.$emit('ai-wrong-reason', {
          reason: payload.reason,
          order: this.selectedOrder,
          missingItems: this.analysisResult?.missingItems || [],
        });
      }
      try {
        // Mark the order as manually correct in the background
        if (this.selectedOrder && this.selectedOrder.id) {
          // The operator did this deliberately and is looking at it — suppress the verdict toast.
          this.markForegroundVerdict(this.selectedOrder.orderId);
          await updateOrderAsCorrect(this.selectedOrder.id);
        }
      } catch (error) {
        logger.error('Error marking order as correct:', { error: error });
      }
      
      this.showAnalysisPopup = false;
      this.analysisResult = null;
      // Entregaron el pedido tal cual: ya no hay re-foto pendiente que recordarles si lo reabren.
      this.forgetRetakePending(this.selectedOrder?.orderId);
      this.clearSelectedOrder();
    },
    handleAnalyzeOrder(event) {
      // A locked-correct order (see _isOrderLockedCorrect) can never turn incorrect, so it
      // is never re-analyzed — we just re-confirm the OK it already got: no photo wait, no
      // API round-trip, no stale "está tardando" warning. This is checked BEFORE the capture
      // race and BEFORE analyzeOrder, so it also short-circuits a new photo taken on a green
      // order (the photo still uploads as evidence via the background uploader; only the
      // pointless re-analysis is skipped). EMPTY_ORDER_CONTENT is NOT locked and still
      // re-analyzes normally, since a new photo can genuinely change that verdict.
      if (this._isOrderLockedCorrect(this.selectedOrder)) {
        this.reconfirmCorrectOrder();
        return;
      }
      if (this.isCapturing) {
        this.pendingAnalyze = true;
        return;
      }
      this.analyzeOrder(event);
    },
    /** Remember the verdict for an order; `null`/`undefined` means "no verdict" and is not stored. */
    rememberVerdict(orderId, isCorrect) {
      if (!orderId || typeof isCorrect !== 'boolean') return;
      this.lastVerdictByOrder = { ...this.lastVerdictByOrder, [orderId]: isCorrect };
      // Un pedido que ya está bien no tiene nada que repetir. Se olvida AQUÍ porque este es el
      // embudo por el que la tablet se entera de un veredicto (HTTP, modal en segundo plano y
      // reconfirmación), así que cubre también las salidas que no pasan por analizar — y no
      // depende de que llegue el evento realtime, que es lo que se pierde con el socket sordo.
      if (isCorrect) this.forgetRetakePending(orderId);
    },
    /** True only when the order's most recent known verdict says it is wrong. */
    isOrderCurrentlyWrong(orderId) {
      if (!orderId) return false;
      const remembered = this.lastVerdictByOrder[orderId];
      if (typeof remembered === 'boolean') return remembered === false;
      // Nothing seen this session — fall back to whatever the list carries (a verdict produced
      // on another tablet, or before this one was opened). It must be a REAL verdict:
      // `order_analysis.is_correct` is DEFAULT false in Postgres and the CV writer inserts the
      // row when the first photo lands without ever setting it, so an order that has never been
      // compared already reads as wrong — and the realtime event pushes that to the tablet
      // seconds after the first photo. `app_response` is written only by a comparison, so it is
      // what separates a verdict from a freshly created row. Without this check every second
      // photo launches an analysis on an order nobody analysed (measured in production as 14
      // photos producing 9 analyses).
      const analyses = this.localRawOrders.find((o) => o.orderId === orderId)?.order?.orderAnalyses;
      return analyses?.isCorrect === false && isDecidedAnalysis(analyses);
    },
    reconfirmCorrectOrder() {
      // Show the same "¡Todo está perfecto!" confirmation an analysis would have produced,
      // straight from the stored verdict. The popup plays the correct sound and auto-dismisses
      // via its own progress bar (emits `close` → closePopup after 2s).
      this.loading = false;
      this.loadingImages = [];
      this.analysisErrorMessage = '';
      this.rememberVerdict(this.selectedOrder?.orderId, true);
      this.analysisResult = { missingItems: [], appResponse: 'ORDER_ANALYZED', isCorrect: true };
      this.showAnalysisPopup = true;
    },
    continueInBackground(from = {}) {
      // The employee chose to keep working while this order finishes analysing.
      if (this.loadingImages.length > 0) {
        // Upload phase — the analysis hasn't started yet. Hand it to the queue (relaunches
        // when the photos finish uploading), reassure with a non-blocking toast, and let go.
        // The running analyzeOrder loop exits by itself once the modal closes.
        const orderKey = this.selectedOrder?.orderId;
        const analysisId = this.selectedOrder?.orderAnalysis || null;
        const label = this.orderDisplayLabel(this.selectedOrder);
        if (orderKey) {
          reportForensicEvent('analysis_backgrounded', {
            order_id: orderKey,
            order_analysis_id: analysisId,
            reason: 'manual',
            phase: 'upload',
            photos_pending: this.loadingImages.length,
          });
          enqueueAnalysis({ orderId: orderKey, analysisId, reason: 'background_manual' });
          // Let the detector toast the queued verdict when it lands — this path has no toast of its own.
          this.unmarkForegroundVerdict(orderKey);
        }
        this.notify({ orderLabel: label, tag: this.$t('message.tagUploading'), body: this.$t('message.bodyQueuedUploading'), variant: 'info' });
        this.showAnalysisPopup = false;
        this.loading = false;
        this.clearSelectedOrder();
        return;
      }
      // Analysis phase — the item-comparison request is already in flight. Mark THIS run as
      // backgrounded (so its resolving result toasts instead of touching the modal), keep it
      // running (it captured its ids in locals) and drop the modal. The verdict arrives as a
      // toast wherever the employee is by then. This is the one backgrounding with no queue
      // event of its own (the run stays alive), so the forensic marker is emitted here.
      reportForensicEvent('analysis_backgrounded', {
        order_id: this.selectedOrder?.orderId ?? null,
        order_analysis_id: this.selectedOrder?.orderAnalysis || null,
        reason: 'manual',
        phase: 'analysis',
      });
      // Y como decisión sobre el aviso, si es que había uno en pantalla.
      this.reportVerdictDecision('background', from);
      this.backgroundedRun = this.activeAnalyzeRun;
      this.showAnalysisPopup = false;
      this.loading = false;
      this.clearSelectedOrder();
    },
    _buildAnalysisResult(result, comparisonTimeMs) {
      // Map the /item-comparison response into the shape the analysis popup renders. Shared by the
      // live analyze path and the "verdict arrived while the operator is back inside the order"
      // path (handleShowVerdictModal), so both produce an identical modal.
      const icons = {
        burger: ':hamburger:', cake: ':cake:', chicken: ':poultry_leg:',
        cup_with_straw: ':cup_with_straw:', french_fries: ':french_fries:',
        sauce: ':bowl_with_spoon:', side: ':takeout_box:',
      };
      return {
        missingItems: Object.entries(result.missing_items || {}).map(([, value]) => {
          const iconString = icons[value.icon] || value.icon || '';
          // O sale un <img> de Twemoji, o no sale nada. `parse()` devuelve la cadena TAL CUAL
          // cuando no reconoce el shortcode (medido: `:fries:` y `fries` vuelven intactos, y
          // `:fries:` está en nuestros propios fixtures), y eso acababa pintándose como texto
          // crudo dentro de la celda del icono, empujando al nombre del producto. Quien consuma
          // esto pone su propio respaldo. No vale comprobarlo con `getEmojiObjectByShortcode`:
          // acepta `chicken`, que `parse()` no resuelve.
          const parsedIcon = uEmojiParser.parse(iconString);
          return {
            groupName: value.group_name,
            icon: parsedIcon.startsWith('<img') ? parsedIcon : '',
            missingQuantity: value.missing_quantity,
            totalQuantity: value.total_quantity,
            name: value.name || [],
          };
        }),
        appResponse: result.app_response,
        description: result.description,
        isCorrect: result.is_correct,
        processingTime: comparisonTimeMs != null ? comparisonTimeMs.toFixed(2) : null,
      };
    },
    // App routes a verdict here (instead of a toast) when it arrives for the order the operator is
    // currently inside — e.g. a queued analysis finishing right after they re-entered it. Show the
    // usual result modal, built from the raw item-comparison result.
    handleShowVerdictModal(event) {
      const detail = event?.detail || {};
      if (!detail.orderId || !detail.result) return;
      if (this.selectedOrder?.orderId !== detail.orderId) return; // they've since left — App's toast covers it
      this.loading = false;
      this.loadingImages = [];
      this.analysisErrorMessage = '';
      this.markForegroundVerdict(detail.orderId);
      const built = this._buildAnalysisResult(detail.result, null);
      this.rememberVerdict(detail.orderId, built.isCorrect);
      // Show FIRST so the popup's <audio> refs (gated by v-if="show") mount, then set the result on
      // the next tick so the analysisResult watcher plays the verdict sound. Mirrors analyzeOrder.
      this.showAnalysisPopup = true;
      this.$nextTick(() => { this.analysisResult = built; });
    },
    notifyAnalysisResult(result, orderLabel) {
      // Same detailed wording + card layout as the queued path (App.handleQueuedVerdict) via
      // the shared builder — the id renders separately, in the rider-toast card.
      const isCorrect = result?.isCorrect === true ||
        (Array.isArray(result?.missingItems) && result.missingItems.length === 0);
      const { tag, body, bodySegments, variant } = analysisResultToast(
        { isCorrect, missingItems: result?.missingItems || [] },
        (k, ...rest) => this.$t(k, ...rest)
      );
      this.notify({ orderLabel, tag, body, bodySegments, variant });
    },
    handleBlackImageDetected() {
      // El componente de cámara ya intentó capturar 3 veces y todas salieron negras. En vez de
      // un modal que obliga a cerrar y re-seleccionar el pedido (lo cual hace perder tiempo al
      // empleado por algo que probablemente se arregla con un nuevo toque al obturador), mostramos
      // un toast persistente y dejamos al empleado intentarlo manualmente sin perder contexto.
      this.isCapturing = false;
      this.pendingAnalyze = false;
      this.showBlackImageHintBriefly();
    },
    showBlackImageHintBriefly() {
      if (this.blackImageHintTimeout) {
        clearTimeout(this.blackImageHintTimeout);
      }
      this.showBlackImageHint = true;
      this.blackImageHintTimeout = setTimeout(() => {
        this.showBlackImageHint = false;
        this.blackImageHintTimeout = null;
      }, HINT_MS.black);
    },
    handlePhotoCaptured(photoData) {
      // The photo belongs to the order that was open when the SHUTTER fired, which
      // CameraPreviewComponent freezes into photoData.order for exactly this — not to whatever is
      // open now. The shutter unlocks before the encode (that is what makes it feel instant), so
      // the photo lands a few hundred ms after the operator was already free to move on. Reading
      // this.selectedOrder here dropped the photo on the floor when they had gone back to the list
      // (never saved, never uploaded, and no event saying so), and filed it under the wrong order
      // when they had opened another. The fallback only covers a caller that does not set `order`.
      const orderKey = photoData.order || this.selectedOrder?.orderId;
      const isOpenOrdersPhoto = Boolean(orderKey) && this.selectedOrder?.orderId === orderKey;
      // The capture in front of the operator only reacts to ITS order's photo. One from an order
      // they already left must neither mark the capture they are doing now as finished, nor fire an
      // Analizar they pressed on this order before this order's own photo exists.
      if (isOpenOrdersPhoto) {
        this.isCapturing = false;
        if (this.pendingAnalyze) {
          this.pendingAnalyze = false;
          this.$nextTick(() => this.analyzeOrder());
        } else if (this.photoAndAnalyze) {
          // The first photo of the visit, from the shutter: Analizar is what comes next.
          this.inviteAnalyze = true;
        }
        this.shotThisVisit = true;
      }
      if (!orderKey) {
        logger.error('handlePhotoCaptured: a photo with no order to attribute it to', { key: photoData.key });
        reportForensicEvent('capture_commit_failed', {
          interaction_id: photoData.interactionId || null,
          order_id: null,
          photo_key: photoData.key,
          reason: 'no_order',
        });
        return;
      }

      // Cache blob so thumbnail persists even after background uploader deletes from IndexedDB.
      // Single-entry cache holding the MOST RECENT photo, whichever order it is for: that is the
      // thumbnail they will want when they come back to the order they just shot and left.
      this.lastPhotoCache = {
        orderId: orderKey,
        blob: photoData.image,
        key: photoData.key
      };
      // The thumbnail on screen is the open order's, so it only changes if this photo is that order's.
      if (isOpenOrdersPhoto) {
        if (this.lastPhotoUrl) {
          URL.revokeObjectURL(this.lastPhotoUrl);
        }
        this.lastPhotoUrl = URL.createObjectURL(photoData.image);
        this.lastPhotoKey = photoData.key;
      }

      this.savePhoto(photoData, orderKey);
    },
    async selectOrder(order) {
      this.selectedOrder = order;
      this.shotThisVisit = false;
      this.inviteAnalyze = false;
      // El recordatorio de "vuelve a hacer la foto" es una superficie de la LISTA: en cuanto se
      // abre un pedido ya no pinta nada, y si no se apaga aquí se queda flotando sobre la cámara
      // del pedido nuevo durante lo que le quede de los 4 s.
      this.hideRetakeReminder();
      this.$emit('order-selection-changed', true); // camera open → parent hides the coverage banner
      // Operator is now building this order in the foreground — suppress its auto-verdict toast.
      this.setOpenCameraOrder(order.orderId);
      this.rbeTicketOpen = false; // RBE: start each order with the ticket collapsed
      reportForensicEvent('order_open', {
        order_id: order.orderId,
        ticket_id: order.ticketId ?? null,
        delivery_id: order.deliveryId ?? null,
      });

      // Volvieron al pedido que dejaron a medias: el recordatorio de a qué hay que hacerle la
      // foto vuelve con ellos. Antes de los `await` a propósito: si sueltan el atrás en esa
      // ventana, `retakePending` aterrizaría sobre un pedido ya cerrado. Si entretanto el pedido
      // quedó correcto, no hay nada que repetir y se olvida en vez de pintarse; el caso inverso
      // —esta tablet sabe que está bien pero la lista aún no— lo cubre `rememberVerdict`.
      const pendingRetake = this.retakePendingByOrder[order.orderId];
      if (pendingRetake) {
        if (this._isOrderLockedCorrect(order)) {
          this.forgetRetakePending(order.orderId);
        } else {
          this.retakeItems = pendingRetake;
          this.retakePending = true;
        }
      }

      this.photoCount = await this.getPhotoCount(order);

      // Get the order from IndexedDB
      const orderKey = order.orderId;
      const localOrder = await getOrderFromIndexedDB(this.db(), orderKey);

      if (localOrder) {
        logger.console('selectOrder: Order found in DB. Updating selectedOrder with existing orderAnalysis');
        this.selectedOrder = {
          ...this.selectedOrder,
          orderAnalysis: localOrder.orderAnalysis
        };
      } else {
        logger.error('selectOrder: Order not found in DB. Creating new orderAnalysis');
      }

      await this.loadLastPhotoForOrder(order.orderId);
    },

    /** Recuerda a qué hay que hacerle la foto en este pedido, para volver a pintarlo si reabren. */
    rememberRetakePending(orderId) {
      if (!orderId || !this.retakeItems.length) return;
      this.retakePendingByOrder = { ...this.retakePendingByOrder, [orderId]: this.retakeItems };
    },
    /** Olvida SOLO lo recordado del pedido (no toca el estado del pedido abierto ni el aviso). */
    forgetRetakePending(orderId) {
      if (!orderId || !(orderId in this.retakePendingByOrder)) return;
      const next = { ...this.retakePendingByOrder };
      delete next[orderId];
      this.retakePendingByOrder = next;
    },
    // Han pulsado Analizar: ya no queda re-foto pendiente, ni en el pedido abierto ni para cuando
    // vuelvan. Se limpia al ARRANCAR el análisis y no al terminar a propósito: pulsar Analizar
    // cierra la intención del operario, y lo que pase a partir de ahí (que falle, que se vaya a
    // segundo plano, que se encole) lo gobierna el flujo de análisis, que tiene sus propios avisos.
    clearRetakePending() {
      this.forgetRetakePending(this.selectedOrder?.orderId);
      if (!this.retakePending) return;
      this.retakePending = false;
      this.retakeItems = [];
    },
    async analyzeOrder() {
      this.clearRetakePending();
      // Safety net: a locked-correct order is never re-analyzed, whatever the entry path
      // (handleAnalyzeOrder already short-circuits, but the capture→pendingAnalyze race and
      // any future caller route through here). Re-confirm the OK instead. EMPTY_ORDER_CONTENT
      // is not locked and falls through to a real analysis.
      if (this._isOrderLockedCorrect(this.selectedOrder)) {
        this.reconfirmCorrectOrder();
        return;
      }

      // Offline: we already know the analysis can't run right now. Don't open the blocking
      // "Analizando pedido" modal or sit through the upload/analysis timeout chain — queue
      // the intent and tell the employee with a non-blocking toast so they keep working. It
      // relaunches automatically on reconnect (analysisQueue). Any photos already taken keep
      // uploading in the background independently.
      if (this.isOnline && this.isOnline() === false) {
        const orderKey = this.selectedOrder.orderId;
        const label = this.orderDisplayLabel(this.selectedOrder);
        enqueueAnalysis({ orderId: orderKey, analysisId: this.selectedOrder.orderAnalysis || null });
        this.notify({ orderLabel: label, tag: this.$t('message.tagOffline'), body: this.$t('message.bodyQueuedOffline'), variant: 'info' });
        this.clearSelectedOrder();
        return;
      }

      // Initialize the analysis popup
      this.showAnalysisPopup = true;
      this.loading = true;
      this.analysisResult = null;
      this.analysisErrorMessage = '';
      const runId = ++this.analyzeSeq; // this run's identity, for the background/foreground guard
      this.activeAnalyzeRun = runId;   // this run now owns the modal
      console.log(this.selectedOrder)
      const dbInstance = this.db();
      const orderKey = this.selectedOrder.orderId;
      // Captured up-front so the rest of the flow (and its result handling) never dereferences
      // this.selectedOrder — it may be cleared mid-flight when the user backgrounds the analysis.
      const analysisIdForQueue = this.selectedOrder.orderAnalysis || null;
      const analysisId = this.selectedOrder.orderAnalysis;
      const orderLabel = this.orderDisplayLabel(this.selectedOrder);
      // Mark up-front so the App verdict detector never double-toasts this order while we own
      // it: the realtime verdict event can race ahead of our own result handling. If the user
      // later backgrounds it DURING UPLOAD, we unmark so its queued result IS toasted by the
      // detector (that path produces no direct toast of its own).
      this.markForegroundVerdict(orderKey);
      // A manual run supersedes any analysis queued for this order while offline.
      cancelPendingAnalysis(orderKey);

      // Do not continue if photos are found in IndexedDB for this order. Photos are removed from
      // IndexedDB when uploaded, so the analysis must not run while any of this order's photos are
      // still queued. Adaptive wait (not a flat timeout): while the upload is actually ADVANCING
      // we keep waiting up to a hard ceiling, and only hand the analysis to the background queue
      // when the upload STALLS. Backgrounding relaunches the analysis the moment the photos
      // finish, so a stalled/offline link loses nothing — but a slow-but-MOVING link must not be
      // cut off, which is what the old flat 5s did (it backgrounded orders that would have landed
      // a beat later).
      const POLL_MS = 500;
      const STALL_MS = 6000;       // no upload advance for this long → background + relaunch on finish
      const HARD_MAX_MS = 25000;   // absolute ceiling ("Continuar en segundo plano" is the manual early exit)
      const startTime = performance.now();
      logger.console('analyzeOrder - Checking for photos in IndexedDB for order: ' + orderKey);
      this.loadingImages = (await getPhotosByOrderId(dbInstance, orderKey));
    
      // Using a class property instead of events for abort signaling
      this.uploadTakingTooLong = false;

      // Monotonic, byte-aware batch progress (0..1): completed photos + partial bytes of the
      // in-flight ones over the peak batch size — the same figure the popup progress bar shows,
      // so "advance" here is exactly what the operator sees move.
      let peakCount = this.loadingImages.length;
      let lastProgress = -1;
      let lastProgressAt = startTime;
      // Set when the wait loop hands off to the background queue (stall vs absolute ceiling),
      // consumed at the enqueue site below so the fx_analysis_backgrounded marker records the
      // real reason + how far the upload had progressed. Null on the all-uploaded success break.
      let bgReason = null;
      let bgProgressPct = 0;
      let bgElapsedMs = 0;

      const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      
      while (this.loadingImages.length > 0 && this.showAnalysisPopup) {
        // Refresh the photos from IndexedDB
        this.loadingImages = (await getPhotosByOrderId(dbInstance, orderKey));
        
        // "Advance" = the monotonic batch progress moved since the last poll (bytes flowing, or a
        // photo finished and left the queue). While it keeps advancing we keep waiting up to
        // HARD_MAX_MS; we only hand off to the background queue when the upload STALLS (no advance
        // for STALL_MS). A slow-but-moving link is no longer cut off at a flat 5s.
        const now = performance.now();
        const remaining = this.loadingImages.length;
        peakCount = Math.max(peakCount, remaining);
        const partial = this.loadingImages.reduce((sum, img) => (
          sum + (img.totalBytes > 0 ? Math.min(1, img.transferredBytes / img.totalBytes) : 0)
        ), 0);
        const progress = peakCount ? (peakCount - remaining + partial) / peakCount : 1;
        if (progress > lastProgress + 0.005) { // real advance
          lastProgress = progress;
          lastProgressAt = now;
        }

        const elapsedTime = now - startTime;
        // Slowness hint intentionally disabled for now (Eric): the "la subida está tardando" text
        // added little. The progress bar still shows movement and the adaptive wait is unchanged;
        // uploadTakingTooLong just never flips, so the warning box never renders.

        // Hand off to the background queue when the upload stalls, or at the absolute ceiling.
        if (now - lastProgressAt > STALL_MS || elapsedTime > HARD_MAX_MS) {
          bgReason = elapsedTime > HARD_MAX_MS ? 'upload_hard_max' : 'upload_stall';
          bgProgressPct = Math.round(progress * 100);
          bgElapsedMs = Math.round(elapsedTime);
          logger.warning(`analyzeOrder - Backgrounding upload: elapsed=${bgElapsedMs}ms stalled=${Math.round(now - lastProgressAt)}ms progress=${bgProgressPct}% reason=${bgReason}`);
          break;
        }

        if (this.loadingImages.length === 0) {
          logger.console('analyzeOrder - All photos uploaded. Continuing with analysis.');
          this.loadingImages = [];
          break;
        }

        // Small delay between polls to keep the CPU idle while we wait on the upload.
        await sleep(POLL_MS);
      }
      
      // If we still have photos, we need to raise an error
      if (this.loadingImages.length > 0) {
        await sleep(500);
        if (this.showAnalysisPopup) {
          logger.error('analyzeOrder - Photos found in IndexedDB for order: ' + orderKey + ' - ' + this.loadingImages);
          // Photos still in the upload queue — offline OR just slow. Either way the analysis
          // must not run without them: queue the intent (it relaunches as soon as the order's
          // photos leave the queue) and say WHY with a NON-BLOCKING toast, so the employee
          // keeps working instead of being stopped by a full-screen modal. Reading an upload
          // delay as "detection failed" is avoided by the explicit copy.
          const autoReason = navigator.onLine ? (bgReason || 'upload_stall') : 'offline';
          reportForensicEvent('analysis_backgrounded', {
            order_id: orderKey,
            order_analysis_id: analysisIdForQueue,
            reason: autoReason,
            phase: 'upload',
            photos_pending: this.loadingImages.length,
            elapsed_ms: bgElapsedMs || null,
            progress_pct: bgProgressPct || 0,
          });
          enqueueAnalysis({ orderId: orderKey, analysisId: analysisIdForQueue, reason: autoReason });
          this.notify(navigator.onLine
            ? { orderLabel, tag: this.$t('message.tagUploading'), body: this.$t('message.bodyQueuedUploading'), variant: 'info' }
            : { orderLabel, tag: this.$t('message.tagOffline'), body: this.$t('message.bodyQueuedOffline'), variant: 'info' });
          this.showAnalysisPopup = false;
          this.loading = false;
          this.clearSelectedOrder();
          return;
        } else {
          logger.console('analyzeOrder - Probably popup was closed. Ignoring photos found in IndexedDB');
          return;
        }
      }

      // Check if we have the analysis ID
      if (!analysisId) {
        logger.error('No order analysis ID found');
        this.showErrorInTimeoutPopup(this.$t('message.analysisIdNotFound'));
        return;
      }

      const preLog = "Analisis ID: " + analysisId + " - ";

      // Reset upload warning since upload phase is complete
      this.uploadTakingTooLong = false;
      this.analysisTakingTooLong = false;

      // Background/foreground routing (D) — declared before the try so both the result and the
      // catch can use them. Read at each decision point, because the user may background THIS
      // run mid-flight. A resolving run only touches the modal if it still owns it
      // (foregroundActive), only toasts if it was backgrounded, and stays silent if a newer
      // run has taken over the UI (so a late result never bleeds into a different order's modal).
      const backgrounded = () => this.backgroundedRun === runId;
      const foregroundActive = () => this.activeAnalyzeRun === runId;
      const reportAnalysisError = (msg) => {
        if (backgrounded()) {
          this.notify({ text: msg, variant: 'warning' });
          this.showAnalysisPopup = false;
        } else if (foregroundActive()) {
          this.showErrorInTimeoutPopup(msg);
        }
      };

      try {
        logger.info(preLog + 'Sending API request to obtain missing items');

        // Measure time for the API request
        const apiStartTime = performance.now();
        const analysisTimeout = 15000; // 7 seconds timeout
        const analysisWarningThreshold = 0.5; // Show warning at 50% (3.5 seconds)

        // Live photo count for THIS order: server-confirmed + still-queued, floored by the
        // peak the upload wait saw. `this.photoCount` is only the select-time server value
        // (0 for a fresh order the operator just photographed) — using it made photos_declared
        // always report 0. getPhotoCount folds in the pending-upload count; peakCount covers
        // the window where photos already left IndexedDB but the server count hasn't caught up.
        const photosDeclared = Math.max(this.getPhotoCount(this.selectedOrder) || 0, peakCount || 0);
        reportForensicEvent('analysis_request', {
          order_id: orderKey,
          order_analysis_id: analysisId,
          photos_declared: photosDeclared,
          path: '/item-comparison',
          restaurant_id: this.restaurant?.id ?? null,
        });

        // Send API request to obtain missing items
        const apiPromise = restPost({
          apiName: 'WEBAPP-API',
          path: '/item-comparison',
          options: {
            body: {
              order_id: orderKey,
              analysis_id: analysisId,
              restaurant_id: this.restaurant.id,
              device_tag: "Default"
            }
          }
        });

        // Create a timeout promise that monitors the elapsed time
        const timeoutPromise = new Promise((_, reject) => {
          const checkInterval = setInterval(() => {
            const elapsedTime = performance.now() - apiStartTime;

            // Show warning at 50% of timeout — only while this run still owns the modal.
            if (foregroundActive() && elapsedTime > analysisTimeout * analysisWarningThreshold && !this.analysisTakingTooLong) {
              logger.console('analyzeOrder - Analysis taking longer than expected: ' + elapsedTime + 'ms');
              this.analysisTakingTooLong = true;
            }

            // Reject when timeout is reached
            if (elapsedTime > analysisTimeout) {
              clearInterval(checkInterval);
              reject(new Error('Analysis timeout'));
            }
          }, 100); // Check every 100ms
        });

        // Race between the API request and the timeout
        const result = await Promise.race([
          apiPromise,
          timeoutPromise
        ]);

        const comparisonTime = performance.now() - apiStartTime;
        logger.success(preLog + 'API Response', result);

        if (result.statusCode === 200 && result.missing_items) {
          const analysisResult = this._buildAnalysisResult(result, comparisonTime);
          this.rememberVerdict(orderKey, analysisResult.isCorrect);

          logger.info(`${preLog} Missing items`, analysisResult);

          // The verdict content, for the forensic stream: the actual result (app_response),
          // how many units were missing, and a compact per-group breakdown (capped so the
          // payload stays small). Emitted ONCE below, after routing, tagged with the surface
          // it was shown on — so "recibí el resultado por toast de segundo plano" is queryable.
          const missingItems = analysisResult.missingItems || [];
          const missingCount = missingItems.reduce((sum, it) => sum + (Number(it.missingQuantity) || 0), 0);
          const missingSummary = missingItems.slice(0, 20).map((it) => ({
            name: it.groupName, missing: it.missingQuantity, total: it.totalQuantity,
          }));
          // Delivery surface: 'foreground' (modal we still own), 'modal' (re-entered the order
          // as the backgrounded verdict landed), 'toast' (background toast elsewhere), 'none'
          // (a newer run owns the UI → nothing shown), 'error' (DB persist failed). Set in each
          // branch below rather than guessed up-front.
          let verdictSurface = 'none';

          // We don't update the app response using the GraphQL mutation anymore since that's handled by the backend

          // Store the analysis result in IndexedDB
          try {
            await updateAnalysisInDB(dbInstance, orderKey, analysisResult);
            logger.console(`${preLog} Analysis result stored in IndexedDB`);

            // Update the orders display
            await this.updateRawOrdersWithLocalData();

            if (backgrounded()) {
              if (this.selectedOrder?.orderId === orderKey) {
                // The operator re-entered this exact order before its backgrounded analysis
                // finished — they're looking at it now, so show the usual result modal here
                // instead of a toast they'd have to tap to act on. Show FIRST so the popup's
                // <audio> refs (gated by v-if="show") are mounted, then set the result on the next
                // tick: the analysisResult watcher plays the verdict sound and only finds the audio
                // elements once shown. Mirrors the live analyze path's ordering.
                this.loading = false;
                this.loadingImages = [];
                this.analysisErrorMessage = '';
                this.markForegroundVerdict(orderKey);
                this.showAnalysisPopup = true;
                this.$nextTick(() => { this.analysisResult = analysisResult; });
                verdictSurface = 'modal';
              } else {
                // Modal is gone and they're elsewhere — deliver the verdict as a non-blocking
                // toast wherever the employee now is (reusing the Rider-toast style).
                this.notifyAnalysisResult(analysisResult, orderLabel);
                verdictSurface = 'toast';
              }
            } else if (foregroundActive()) {
              // We still own the modal — show the result there as usual, and mark the order so
              // the App-level verdict detector doesn't also toast it when the realtime refresh lands.
              this.analysisResult = analysisResult;
              this.markForegroundVerdict(orderKey);
              verdictSurface = 'foreground';
            }
          } catch (dbError) {
            logger.error(`${preLog} Error updating analysis result in IndexDB ${dbError}`,{error: dbError,});
            reportAnalysisError(this.$t('message.analysisSaveError'));
            verdictSurface = 'error';
          }

          reportForensicEvent('analysis_verdict', {
            order_id: orderKey,
            order_analysis_id: analysisId,
            is_correct: result.is_correct === true,
            source: 'http',
            surface: verdictSurface,
            latency_ms: Math.round(comparisonTime),
            app_response: result.app_response ?? null,
            missing_count: missingCount,
            missing_groups: missingSummary.length,
            missing_items: missingSummary,
          });

        } else if (result.statusCode === 408 && result.error) {
          // Backend timed out waiting for detections. Persist what came back (never shown in
          // the modal — missingItems is null) and surface the retry hint via the router.
          await updateAppResponseResults(analysisId, {
            missingItems: null,
            appResponse: result.app_response,
            description: result.description,
            isCorrect: result.is_correct
          });
          logger.error(`${preLog} Timeout en la API de comparativa: ${result.error}`, {error: result.error});
          // The comparison returned no verdict (backend timed out waiting for detections).
          // Record it as a verdict outcome so the stream shows the analysis resolved-without-result,
          // not just a gap after the request.
          reportForensicEvent('analysis_verdict', {
            order_id: orderKey,
            order_analysis_id: analysisId,
            is_correct: null,
            source: 'http',
            surface: 'error',
            backend_status: 408,
            app_response: result.app_response ?? null,
            latency_ms: Math.round(comparisonTime),
          });
          reportAnalysisError(this.$t('message.analysisFailedRetry'));
        } else {
          logger.error(`${preLog} Invalid API response: ${result}`, {error: result});
          throw new Error('Invalid API response');
        }
      } catch (error) {
        logger.error(`${preLog} Error during analysis ${error}`, {error: error});

        // If it's the analysis timeout, close the popup (if we still own it) and keep the
        // order selected so the user can retry. A backgrounded run that timed out is handed
        // to the queue so it retries; the verdict then arrives as the order updates.
        if (error.message === 'Analysis timeout') {
          logger.warning(`${preLog} Analysis timeout after 15 seconds`);
          if (backgrounded()) {
            enqueueAnalysis({ orderId: orderKey, analysisId: analysisIdForQueue, reason: 'analysis_timeout' });
          } else if (foregroundActive()) {
            this.showAnalysisPopup = false;
            this.loading = false;
            this.analysisTakingTooLong = false;
          }
          return;
        }

        // Connection-level failure: queue the intent instead of losing it (relaunches on
        // reconnect) and surface it non-blocking, so the employee keeps working rather than
        // being stopped by a full-screen modal. (loading/analysisTakingTooLong reset in finally.)
        if (!navigator.onLine || error.message === 'Network error') {
          enqueueAnalysis({ orderId: orderKey, analysisId: analysisIdForQueue, reason: navigator.onLine ? 'network_error' : 'offline' });
          if (backgrounded() || foregroundActive()) {
            this.notify({ orderLabel, tag: this.$t('message.tagOffline'), body: this.$t('message.bodyQueuedOffline'), variant: 'info' });
            this.showAnalysisPopup = false;
            this.clearSelectedOrder();
          }
          return;
        }

        // For other errors, route through the same background/foreground logic.
        let errorMessage = 'Error en el análisis. Por favor, intenta de nuevo.';
        if (error instanceof TypeError) {
          errorMessage = 'Error en el formato de la respuesta. Por favor, intenta de nuevo.';
        } else if (error.name === 'AbortError') {
          errorMessage = 'La solicitud ha sido cancelada. Por favor, intenta de nuevo.';
        } else if (error.name === 'TimeoutError') {
          errorMessage = 'La solicitud ha excedido el tiempo de espera. Por favor, intenta de nuevo.';
        }
        reportAnalysisError(errorMessage);
      } finally {
        logger.console('Setting loading to false');
        // Only the run that still owns the modal may clear its loading flags — a resolving
        // backgrounded/superseded run must not turn off a newer foreground run's spinner.
        if (foregroundActive()) {
          this.loading = false;
          this.analysisTakingTooLong = false;
        }
      }
    },
    showErrorInTimeoutPopup(errorMessage, title = '') {
      this.loading = false;
      this.analysisErrorMessage = errorMessage;
      this.timeoutPopupTitle = title;
      this.showAnalysisPopup = false;
      this.showTimeoutPopup = true;
    },
    closeTimeoutPopup() {
      this.showTimeoutPopup = false;
      this.analysisErrorMessage = '';
      this.timeoutPopupTitle = '';
      this.clearSelectedOrder();
    },
    /**
     * Qué decide la cocina cuando el análisis dice que falta un producto.
     *
     * Este es el evento que faltaba para poder medir nada de esta pantalla: hasta ahora sabíamos
     * que el veredicto se mostró (`analysis_verdict`) y, solo en el piloto, el motivo de entregar
     * sin corregir — pero no la decisión en sí, que es la conducta. Sin él, el brazo de control del
     * experimento de IA wrong no emite NADA cuando entregan sin corregir, y no hay con qué
     * comparar: ni denominador, ni reparto corregir/entregar, ni coste en segundos de preguntar.
     *
     * Se emite en los dos brazos y lleva el brazo dentro (`reasons_enabled`), para que la lectura
     * no dependa de reconstruir a posteriori quién tenía el piloto encendido.
     *
     * Solo cuenta cuando había productos marcados como que faltaban: es el aviso que estamos
     * midiendo, no los cierres de un veredicto correcto.
     */
    /**
     * @param {object} from  lo que emitió el modal: `asked` es el brazo TAL Y COMO SE PINTÓ.
     *   No se vuelve a leer el flag aquí a propósito: `isFeatureEnabled` devuelve `undefined`
     *   hasta que responde `/flags`, así que el primer modal tras una recarga podía pintarse con
     *   la pregunta y registrarse como control. El brazo lo decide quien lo enseña.
     */
    reportVerdictDecision(decision, from = {}) {
      const payload = verdictDecisionPayload({
        decision,
        order: this.selectedOrder,
        missingItems: this.analysisResult?.missingItems || [],
        photos: this.photoCount ?? null,
        shownAt: this.verdictShownAt,
        reasonsEnabled: from.asked === true,
        reason: from.reason || null,
        reasonMs: from.reason_ms ?? null,
      });
      if (payload) reportForensicEvent('verdict_decision', payload);
    },
    closePopup(from = {}) {
      // Cerrar con la X es una salida más: sin esto, el pedido se queda sin decisión registrada.
      this.reportVerdictDecision('dismiss', from);
      this.showAnalysisPopup = false;
      this.loading = false;
      this.analysisResult = null; // Clear the analysis result when closing
      this.clearSelectedOrder();
    },
    addNewPictures(from = {}) {
      this.reportVerdictDecision('retake', from);
      this.showAnalysisPopup = false; // Hide popup
      // Antes de tirar el resultado, quedarse con lo que faltaba: es lo que hay que fotografiar.
      this.retakeItems = this.analysisResult?.missingItems || [];
      this.analysisResult = null;
      this.loading = false;
      // A partir de aquí hay una foto pendiente. Pueden salir del pedido, pero se les recuerda.
      this.retakePending = true;
      // A new photo is what is asked for: «Foto y analizar» again.
      this.shotThisVisit = false;
      this.inviteAnalyze = false;
    },
    handleBackToHome(reason = 'back_button') {
      const order = this.selectedOrder;
      // Se van con la re-foto pendiente. Hasta v4.49.9 esto no se podía: el atrás era un candado
      // y la única salida era analizar. El guardarraíl que se puso para comprobar que no atrapaba
      // a nadie de más dijo lo contrario — 2.111 topes contra el candado en 259 tiendas en una
      // semana —, así que ahora se sale y se recuerda. El evento cambia de nombre con el
      // comportamiento: ya no mide un bloqueo, mide una salida con la foto a medias.
      const leavingWithRetake = Boolean(this.retakePending && order);
      if (leavingWithRetake) {
        this.rememberRetakePending(order.orderId);
        reportForensicEvent('retake_back_exited', {
          order_id: order.orderId,
          order_analysis_id: order.orderAnalysis || null,
          // Contado AHORA, no `this.photoCount`: ese es el valor de cuando se abrió el pedido y
          // aquí la pregunta es justo la contraria — si llegaron a hacer la foto antes de irse,
          // que es lo que separa una salida legítima de un escape del formulario de motivos.
          photos: this.getPhotoCount(order),
          items: this.retakeItems.length,
          reason,
        });
      }
      // Operator left the order screen back to home WITHOUT analysing — the back arrow, or the
      // "Finalizar" button on non-Pro restaurants (both emit clear-order). This is the one exit
      // that had no forensic marker: every other close is implied by its verdict/backgrounded
      // event, so an order_open with no matching close/verdict was previously a blind spot
      // (abandoned order). Pairs with fx_order_open by order_id. photos = whether they had
      // captured anything before leaving (photos>0 + no verdict = a real abandon worth seeing).
      if (order) {
        reportForensicEvent('order_close', {
          order_id: order.orderId,
          order_analysis_id: order.orderAnalysis || null,
          reason,
          photos: this.getPhotoCount(order),
        });
      }
      this.clearSelectedOrder();
      if (leavingWithRetake) this.showRetakeReminderToast(this.orderDisplayLabel(order));
    },
    showRetakeReminderToast(code = null) {
      this.retakeReminderCode = code || null;
      this.showRetakeReminder = true;
      clearTimeout(this.retakeReminderTimeout);
      this.retakeReminderTimeout = setTimeout(() => {
        this.showRetakeReminder = false;
        this.retakeReminderTimeout = null;
      }, HINT_MS.retake);
    },
    hideRetakeReminder() {
      this.showRetakeReminder = false;
      clearTimeout(this.retakeReminderTimeout);
      this.retakeReminderTimeout = null;
    },
    clearSelectedOrder() {
      this.retakePending = false;
      this.retakeItems = [];
      this.pendingAnalyze = false;
      this.shotThisVisit = false;
      this.inviteAnalyze = false;
      this.isCapturing = false;
      this.rbeTicketOpen = false;
      if (this.lastPhotoUrl) {
        URL.revokeObjectURL(this.lastPhotoUrl);
        this.lastPhotoUrl = null;
        this.lastPhotoKey = null;
      }
      this.selectedOrder = null;
      this.$emit('order-selection-changed', false); // back to the list → banner may show again
      this.clearOpenCameraOrder();
    },
    async savePhoto(photoData, orderKey) {
      const dbInstance = this.db();
      if (!dbInstance) {
        logger.error('Database instance is not available');
        return;
      }

      // Timed from the FINGER, not from here: this is the chain ops actually complains about
      // ("between pressing the shutter and the Analyse button appearing it takes ages"), and every
      // step of it runs on the main thread behind whatever else is busy.
      const tapAt = photoData.tapAt ?? null;
      const since = () => (tapAt != null ? Math.round(performance.now() - tapAt) : null);
      const stages = {};
      try {
        // Save photo
        let transaction = dbInstance.transaction('photos', 'readwrite');
        let store = transaction.objectStore('photos');
        await new Promise((resolve, reject) => {
          const request = store.add(photoData);
          request.onsuccess = resolve;
          request.onerror = (event) => reject(new Error(`Failed to save photo: ${event.target.error}`));
        });

        stages.t_idb_add = since();

        // Get the order from IndexedDB to update the photos array
        let order = await getOrderFromIndexedDB(dbInstance, orderKey);

        // Update the photos array
        order.order.photos.push(photoData.key);

        // Save the order to IndexedDB
        await saveOrderToIndexedDB(dbInstance, order);

        // A photo taken after a FAILED verdict used to change nothing. The CV analyses it and
        // its detections join the union, but only the "Analizar" button ever recomputes the
        // verdict — so an order the operator has already fixed keeps a verdict naming an item
        // its own detections contain, and is counted as an AI failure instead of a save.
        // Queue a re-analysis instead of calling the endpoint here: the queue holds the intent
        // while the photo is still in the upload queue and fires the moment it lands
        // (hasPendingPhotos / onPhotoUploaded), drops any earlier intent for the same order so a
        // burst of frames produces one run, and yields to a manual press
        // (analyzeOrder -> cancelPendingAnalysis).
        //
        // ONLY after a failed verdict, and the two exclusions are the reason this was never done
        // before. An order with no verdict yet must not be analysed by a photo, or every order
        // with more than one photo would read as intervened by Bronze. An order already correct
        // must not be re-opened. After a failure the order is irreversibly marked intervened, so
        // a later run can only move it from ia_wrong to solved.
        //
        // This must stay at the END of savePhoto: enqueueing before the photo row exists makes
        // hasPendingPhotos false, the queue fires at once, and the comparison runs before the
        // upload — the very race being fixed.
        // Not for a live-demo order: there the presenter presses Analizar themselves, which keeps the scripted
        // verdicts deterministic (src/liveDemo/).
        if (this.isOrderCurrentlyWrong(orderKey) && !isLiveDemoId(orderKey)) {
          enqueueAnalysis({
            orderId: orderKey,
            // The analysis of THE PHOTO'S order, from the record just saved — the same id the
            // background uploader attaches to this photo. Not this.selectedOrder: by now the
            // operator may be inside another order (its analysis id, with this order's key) or on
            // the list (null, a TypeError that read as a storage failure).
            analysisId: order.orderAnalysis || null,
            reason: 'late-photo',
          });
        }

        // The new photo is counted through pendingPhotoKeys (it sits in the upload
        // queue until the background uploader ships it) — NOT by bumping photoCount,
        // which mirrors the server and gets overwritten by every full fetch.
        stages.t_order_saved = since();
        await this.refreshPendingPhotoKeys(orderKey);
        stages.t_keys = since();
        const updatedOrderIndex = this.localRawOrders.findIndex(o =>
          o.orderId === orderKey
        );
        if (updatedOrderIndex === -1) {
          // If the order is not in localRawOrders, add it — the PHOTO's order: the open one while
          // it still is, otherwise the record just read back. Never whichever order is open now.
          const photoOrder = this.selectedOrder?.orderId === orderKey ? this.selectedOrder : order;
          this.localRawOrders.push({ ...photoOrder });
        }

        // Record the capture AFTER the order is guaranteed to be in localRawOrders.
        // getPhotoCount counts DISTINCT photo keys, and until this key is known the same
        // photo is counted twice the moment photo_created lands: once as queued, once as
        // server-confirmed — the 1 -> 2 -> 1 flicker measured on the tablet after a single
        // photo. It also keeps the count (and the "Analyse" button) alive if that
        // confirmation never arrives.
        this.rememberLocalPhotoKey(orderKey, photoData.key);

        // The button is `v-if="photoCount > 0"`, so the state change is not the button on screen:
        // Vue flushes in a microtask and the pixels land on the next frame. One rAF, not a chain,
        // and it is cancelled by nothing because it fires exactly once.
        if (tapAt != null) {
          requestAnimationFrame(() => {
            reportForensicEvent('capture_committed', {
              interaction_id: photoData.interactionId || null,
              order_id: orderKey,
              photo_key: photoData.key,
              ...stages,
              t_painted: since(),
              orders_count: this.localRawOrders.length,
            });
          });
        }

        this.$emit('photo-saved', orderKey);

        // Live-demo order: nothing leaves the device, so the upload the background uploader would do is
        // replayed locally (progress, record leaves the queue, photoUploaded). Fire-and-forget.
        if (isLiveDemoId(orderKey)) {
          import('@/liveDemo/live').then(({ simulatePhotoUpload }) => simulatePhotoUpload(dbInstance, photoData.key));
        }

        // Show duplicate hint if 2nd+ photo taken immediately after cooldown ended — on the order
        // on screen only. If they already left it there is nothing there to warn about, and
        // getPhotoCount(null) threw here, AFTER the photo was saved, and logged it as a failure.
        if (this.selectedOrder?.orderId === orderKey) {
          const photoCount = this.getPhotoCount(this.selectedOrder);
          if (photoCount >= 2 && this.lastCooldownEndTime && (Date.now() - this.lastCooldownEndTime < 4000)) {
            this.showDuplicateHintBriefly();
          }
        }
      } catch (error) {
        logger.error('Error saving photo:', {error: error});
        // A capture_saved with no capture_committed has to be readable as "IndexedDB failed",
        // not as "the event got lost": otherwise a storage fault looks like telemetry loss.
        reportForensicEvent('capture_commit_failed', {
          interaction_id: photoData.interactionId || null,
          order_id: orderKey,
          photo_key: photoData.key,
          ...stages,
          reason: String(error?.name || error).slice(0, 80),
        });
      }
    },
    async loadLastPhotoForOrder(orderId) {
      // Check in-memory cache first (survives background uploader deletion)
      if (this.lastPhotoCache && this.lastPhotoCache.orderId === orderId) {
        this.lastPhotoUrl = URL.createObjectURL(this.lastPhotoCache.blob);
        this.lastPhotoKey = this.lastPhotoCache.key;
        return;
      }

      // Fall back to IndexedDB
      try {
        const dbInstance = this.db();
        const photos = await getPhotosByOrderId(dbInstance, orderId);

        if (photos.length > 0) {
          photos.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          const lastPhoto = photos[0];
          this.lastPhotoUrl = URL.createObjectURL(lastPhoto.image);
          this.lastPhotoKey = lastPhoto.key;
          this.lastPhotoCache = { orderId, blob: lastPhoto.image, key: lastPhoto.key };
        } else {
          this.lastPhotoUrl = null;
          this.lastPhotoKey = null;
        }
      } catch (error) {
        logger.error('Error loading last photo for order:', { error });
        this.lastPhotoUrl = null;
        this.lastPhotoKey = null;
      }
    },
    async healthCheck() {
      /**
       * This method will perform a health check by updating the last alive timestamp in the database.
       */
      // Update the last alive timestamp. We need the device ID as first argument and appVersion as second argument.
      const device = await updateLastAlive(versionInternal);
      // The same call carries the server's view of the orders channel; App.vue owns the ledger and the socket.
      if (device) this.$emit('device-synced', device);
    },
  },
};
</script>

<style scoped>
/* The lookup card: the system's scrim and raised card, as the timeout popup draws them. */
.duplicate-lookup {
  position: fixed;
  inset: 0;
  box-sizing: border-box;
  padding: var(--bz-space-4);
  background-color: var(--bz-scrim);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--bz-z-lane-popup);
}
.duplicate-lookup__card {
  box-sizing: border-box;
  width: min(var(--bz-dialog-width), 100%);
  padding: var(--bz-space-6);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-xl);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--bz-space-3);
  font-family: var(--bz-font);
}
.duplicate-lookup__icon {
  font-size: var(--bz-icon-lg);
  color: var(--bz-ink-2);
}
.duplicate-lookup__text {
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  color: var(--bz-ink-2);
  text-align: center;
}

.camera-screen {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  background-color: var(--bz-p-0c1117);
  /* The system zoom is free (norm 9, public/index.html); only here, where a gloved pinch would
     enlarge the service screen by accident, it pans and does not zoom. */
  touch-action: pan-x pan-y;
}

/* RBE pilot — mobile only: compact order-code bar ABOVE the camera preview (icon + short code).
   Hidden on tablet/desktop (they keep the immersive layout). */
.rbe-order-bar {
  display: none;
}
@media (max-width: 767px) {
  .camera-screen.rbe-cam .rbe-order-bar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    flex: 0 0 auto;
    box-sizing: border-box;
    /* 18px = app-standard side margin (order list buttons / header) */
    padding: 8px 18px;
    background-color: var(--bz-p-0c1117);
    z-index: var(--bz-z-order-code);
  }
  .rbe-order-bar-icon {
    width: 35px;
    height: 35px;
    object-fit: contain;
    border-radius: 9px;
    flex: 0 0 auto;
  }
  .rbe-order-bar-code {
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    font-size: 22px;
    color: var(--bz-p-white);
    white-space: nowrap;
  }
  /* Ticket toggle lives inside the top bar, anchored right, so the code (left) and the button never
     overlap and the button stays in the same spot whether it reads "Ticket" or "Ocultar". */
  .rbe-ticket-toggle {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    background-color: var(--bz-p-blue-400);
    color: var(--bz-p-blue-950);
    border: none;
    border-radius: 999px;
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
    padding: 9px 14px;
    cursor: pointer;
  }
  .rbe-ticket-toggle--open {
    background-color: var(--bz-p-slate-200);
  }
  /* Mobile is not immersive-absolute, so let the camera fill the space left below the bar. */
  .camera-screen.rbe-cam :deep(.camera-preview) {
    flex: 1 1 auto;
    min-height: 0;
  }
}



/* Arriba a la izquierda: el único sitio libre de la captura (el código del pedido ocupa la
   derecha y los controles el bajo) y el primero que se mira al levantar la vista de la bolsa. */
/* Qué hay que volver a fotografiar, encima del visor. Franja z de avisos sobre la cámara: por
   debajo de .last-photo-stack (999) y de .duplicate-hint (1000), por encima del header inmersivo.
   No recibe toques. Un solo acento (ámbar): el borde, que es lo que recorta el panel contra un
   vídeo de cualquier color, y la píldora de cantidad. Los tokens son los del aviso de cobertura
   y el flujo de ayuda, para no abrir un cuarto juego de grises. */
.retake-aim {
  --sheet: var(--bz-p-17202b);
  --hairline: var(--bz-line--white-a9);
  --ink: var(--bz-ink--slate-50);
  --ink-secondary: var(--bz-p-slate-300);
  --ink-muted: var(--bz-p-slate-400);
  --amber: var(--bz-p-f5c451);
  --amber-ink: var(--bz-p-281a02);

  position: absolute;
  top: 70px;
  left: 20px;
  z-index: var(--bz-z-retake-aim);
  width: auto;
  max-width: min(400px, calc(100% - 40px));
  box-sizing: border-box;
  padding: 12px 16px;
  border-radius: 16px;
  /* Opaco de verdad: esto se lee sobre el vídeo de la cámara, que puede ser de cualquier color. */
  background: var(--sheet);
  /* 2 px y no 1,5: en DPR 1 Chrome redondea 1,5 a 1, y contra un vídeo oscuro el borde ES la
     silueta del panel (el fondo queda a 1,15:1 de un #0C1117). Sin sombra: el halo oscurecía
     28 px de visor alrededor sin aportar lectura. */
  border: 2px solid var(--amber);
  font-family: "DM Sans", sans-serif;
  pointer-events: none;
}

.retake-aim__head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--ink-secondary);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.retake-aim__head .pi {
  font-size: 16px;
}

.retake-aim__items {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
}

.retake-aim__item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 0;
  box-sizing: border-box;
}

/* Ritmo entre filas, no silueta: a propósito por debajo del contraste de borde. */
.retake-aim__item + .retake-aim__item,
.retake-aim__more {
  border-top: 1px solid var(--hairline);
}

/* Caja fija y recortada. El icono llega por v-html como <img> de Twemoji, y lo que entre aquí
   no puede empujar ni solapar al nombre del producto pase lo que pase. */
.retake-aim__emoji {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  line-height: 1;
}

.retake-aim__emoji :deep(img) {
  display: block;
  width: 26px;
  height: 26px;
  position: relative;
}

/* La imagen viene de un CDN y estas tablets se quedan sin red. Si no carga, se pinta el glifo
   del alt dentro de la misma caja, en lugar del icono de imagen rota. */
.retake-aim__emoji :deep(img::after) {
  content: attr(alt);
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sheet);
  font-size: 22px;
  line-height: 1;
}

/* Lo único blanco y en 700: es el dato que la persona ha venido a buscar. */
.retake-aim__name {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--ink);
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.retake-aim__qty {
  flex: 0 0 auto;
  background: var(--amber);
  color: var(--amber-ink);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  padding: 6px 14px;
  border-radius: 20px;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

/* Alineada con los nombres (26 del icono + 10 del gap). */
.retake-aim__more {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 6px 0 6px 36px;
  box-sizing: border-box;
  color: var(--ink-secondary);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.retake-aim__foot {
  margin-top: 6px;
  color: var(--ink-muted);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}

.duplicate-hint {
  position: absolute;
  bottom: 240px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bz-p-yellow-100);
  border: 1px solid var(--bz-p-amber-500);
  border-radius: 24px;
  padding: 10px 22px;
  color: var(--bz-p-amber-800);
  font-family: "DM Sans", sans-serif;
  font-size: 18px;
  font-weight: 600;
  pointer-events: none;
  z-index: var(--bz-z-duplicate-hint);
  white-space: nowrap;
  box-shadow: 0 2px 12px var(--bz-p-black-a15);
  display: flex;
  align-items: center;
  gap: 8px;
}

.duplicate-hint .pi-exclamation-triangle {
  font-size: 20px;
  color: var(--bz-p-amber-500);
}

/* Error variant for the black-image-detected toast. The default warning yellow blends in too much
   for a "your last 3 captures all failed" signal, which the employee really needs to act on. */
.duplicate-hint--error {
  background: var(--bz-p-red-100);
  border-color: var(--bz-p-red-600);
  color: var(--bz-p-red-800);
  max-width: min(80vw, 520px);
  white-space: normal;
  text-align: center;
  line-height: 1.3;
}

/* Para un aviso que no cabe en una línea. `.duplicate-hint` es `nowrap` y va centrado con
   `translateX(-50%)`, así que lo que desborda se recorta por los DOS lados: el recordatorio del
   retake ronda los 45-49 caracteres (más en EN y FR) frente a los 33 del aviso de foto repetida.
   `width: max-content` no es adorno: posicionado con `left: 50%` y sin `right`, el ancho que el
   navegador tiene para repartir es solo la mitad derecha del contenedor, así que sin esto el
   `max-width` nunca llega a aplicarse y en móvil el aviso se estrangula a 187 px y cuatro líneas
   (medido). Con él manda el contenido, y el `max-width` es quien lo envuelve. */
.duplicate-hint--wrap {
  width: max-content;
  max-width: min(80vw, 520px);
  white-space: normal;
  text-align: center;
  line-height: 1.3;
}

.duplicate-hint--error .pi-times-circle {
  font-size: 20px;
  color: var(--bz-p-red-600);
  flex-shrink: 0;
}

.hint-fade-enter-active {
  transition: opacity 0.3s ease;
}

.hint-fade-leave-active {
  transition: opacity 0.6s ease;
}

.hint-fade-enter-from,
.hint-fade-leave-to {
  opacity: 0;
}

/* Photo stack container */
.last-photo-stack {
  position: absolute;
  bottom: 42px;
  left: calc(75% - 63px);
  transform: translateX(-50%);
  width: 130px;
  z-index: var(--bz-z-photo-stack);
  pointer-events: none;
}

/* Stacking cards behind (pseudo-elements) */
.last-photo-stack::before,
.last-photo-stack::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 10px;
  border: 3px solid var(--bz-p-white-a50);
  background: var(--bz-p-1a2332);
  display: none;
}

.last-photo-stack--multi::after {
  display: block;
  transform: translate(5px, 5px) rotate(1.5deg);
  z-index: 1;
}

.last-photo-stack--many::before {
  display: block;
  transform: translate(10px, 10px) rotate(3deg);
  z-index: 0;
}

.last-photo-stack--many::after {
  display: block;
  transform: translate(5px, 5px) rotate(1.5deg);
  z-index: 1;
}

/* Photo thumbnail */
.last-photo-thumb {
  position: relative;
  border-radius: 10px;
  border: 3px solid var(--bz-p-white);
  overflow: hidden;
  z-index: 2;
  box-shadow: 0 4px 20px var(--bz-p-black-a35);
  height: 88px;
}

.last-photo-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Transitions */
.thumb-fade-enter-active {
  transition: opacity 0.15s ease;
}

.thumb-fade-leave-active {
  transition: opacity 0.2s ease;
}

.thumb-fade-enter-from,
.thumb-fade-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .duplicate-hint {
    bottom: 200px;
    font-size: 15px;
    padding: 8px 18px;
  }

  /* Por debajo del código de pedido, que en móvil va centrado a top:70 y mide 63 de alto. A 12
     el panel caía encima del header: aquí el bloque contenedor es el body, no la pantalla. */
  .retake-aim {
    top: 141px;
    left: 12px;
    max-width: calc(100% - 24px);
    padding: 10px 14px;
  }

  .retake-aim__head { font-size: 15px; }
  .retake-aim__item, .retake-aim__more { min-height: 40px; padding-top: 5px; padding-bottom: 5px; }
  .retake-aim__more { padding-left: 34px; font-size: 15px; }
  .retake-aim__emoji { flex-basis: 24px; width: 24px; height: 24px; font-size: 20px; }
  .retake-aim__emoji :deep(img) { width: 24px; height: 24px; }
  .retake-aim__emoji :deep(img::after) { font-size: 20px; }
  .retake-aim__name { font-size: 18px; }
  .retake-aim__qty { font-size: 15px; padding: 5px 12px; }
  .retake-aim__foot { font-size: 14px; }

  .last-photo-stack {
    width: 134px;
    bottom: 160px;
    left: auto;
    right: 12px;
    transform: none;
  }

  .last-photo-thumb {
    height: auto;
  }

  .last-photo-thumb img {
    height: auto;
    max-height: none;
    object-fit: contain;
  }
}

/* Immersive mode — tablet only */
@media (min-width: 768px) {
  .immersive-mode {
    position: relative;
  }

  /* Camera preview fills the entire viewport behind everything */
  .immersive-mode :deep(.camera-preview) {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100%;
    height: 100% !important;
    z-index: var(--bz-z-camera-preview);
  }

  .immersive-mode :deep(.camera-preview),
  .immersive-mode :deep(.preview-video) {
    border-radius: 0;
  }

  /* Semitransparent header overlay */
  .immersive-mode :deep(.header) {
    background-color: var(--bz-surface--0c1117-a10);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    position: relative;
    z-index: var(--bz-z-header);
  }

  /* Header buttons — translucent glass style */
  .immersive-mode :deep(.header-button) {
    background: var(--bz-p-white-a12) !important;
    border: none !important;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    box-shadow: inset 0 0 0 1px var(--bz-p-white-a20), inset 0 1px 0 var(--bz-shade--white-a15);
  }

  @media (hover: hover) {
    .immersive-mode :deep(.header-button:hover) {
      background: var(--bz-p-white-a20) !important;
    }
  }

  /* Rider counter in alert state — the loudest surface in the header: an un-photoed
     order with a rider already waiting. It outranks the amber help button, so it is
     the least glassy thing on screen on purpose.
     The fill token is #B91C1C, the same red the opaque state uses — not the #DC2626
     that was here. With #DC2626 the white number only clears AA at alpha 0.935, i.e.
     with no glass left at all; #B91C1C clears it at 0.78, so 0.88 keeps 12% of the
     video modulating the fill and still lands at 5.41:1 over a white delivery bag
     (7.77:1 over black). The old 0.28 gave 1.57:1 there — the glyph and the number
     were simply not on screen.
     The content stays white, and that is the only polarity that works: the amber's
     #281A02 ink would read 2.18:1 over black and 3.14:1 over white, failing at BOTH
     ends — the exact mirror of why the amber ink had to stay dark. */
  .immersive-mode :deep(.rider-counter.alert) {
    background: var(--bz-p-red-700-a88) !important;
    /* One hairline, not the amber's two: this fill is dark enough to hold its own
       silhouette against a white bag (5.41:1), so the rim is only load-bearing at the
       black end. Light red, never white — white is the settings cog's language, and
       the opaque state's own bevel is a light red too. 0.65 is the alpha that
       maximises the contour's worst case: 4.37:1 over black, 4.26:1 over white.
       The bevel lives in `outline`, NOT in box-shadow, because .rider-counter.urgent
       animates box-shadow: an animation owns the property outright, so an inset bevel
       declared here is absent for the whole time the pulse runs (measured in Chrome,
       not inferred). outline-offset: -1px puts the ring exactly where the inset shadow
       drew it and follows the 9px radius, at no layout cost.
       This is not a focus ring — do not sweep it with App.vue's `outline: none`. */
    outline: 1px solid var(--bz-p-red-500-a65);
    outline-offset: -1px;
    /* Clears the white glass bevel inherited from .header-button above. NEVER add
       !important here: important author declarations outrank CSS animations, so
       `box-shadow: … !important` would silently kill the urgent pulse. Without it the
       keyframes take the property over cleanly and the outline bevel rides through. */
    box-shadow: none;
  }

  /* Hover mirrors the opaque state: same alpha, darker red (#991B1B), so legibility
     only ever goes up — 6.58:1 over white, 9.70:1 over black. That matters because
     Android leaves :hover stuck after a tap (see the help button): a stuck hover here
     is harmless by construction. Only the background needs restating — the generic
     hover rule above touches nothing else, so the bevel survives. */
  @media (hover: hover) {
    .immersive-mode :deep(.rider-counter.alert:hover) {
      background: var(--bz-p-red-800-a88) !important;
    }
  }

  /* Help button in immersive mode: the same iOS-style liquid glass as the settings
     cog, in amber. Same two-shadow bevel, same 1px weights, same inherited blur —
     only the tint differs, which is what has to keep the two buttons apart.
     Why the fill stays at 0.70 and not the cog's 0.12: the #281A02 ink drops below
     4.5:1 at alpha 0.6492 over a dark tray, so 0.65 is a hard floor. And the glass
     was never coming from the fill anyway — at 0.70 the amber still tracks 43% of
     the video's luminance swing (dL 0.4264 against the cog's 0.9866), while going
     down to 0.65 buys only 13.8% more swing and eats the whole AA margin.
     What read as paint was the bevel: ink 0.55 stacked over light 0.80 is 91%
     opaque, so the contour stopped responding to the video altogether — dL 0.0209
     against the cog's 0.9287. That was a frame, not a reflection. */
  .immersive-mode :deep(.header-button.help-button) {
    background: var(--bz-p-f5c451-a70) !important;
    /* Rebuilt as insets because `border: none !important` above kills the
       border-box gradient. One 1px ring at 0.35 plus a top-edge-only highlight:
       35% opaque on three sides, dL 0.3074, so the canto moves with the video.
       Ring listed first and highlight second — same order as the generic rule, so
       both buttons catch light identically. Ring is #FFDA8A rather than white so
       the edge stays warm; the highlight is white because it is the light source,
       and at 0.30 over amber it still reads warm. Static shadow — nothing animates,
       so none of the main-thread cost of the old capture-pulse.
       backdrop-filter is deliberately NOT restated: it inherits blur(6px) from the
       generic rule, and matching the cog's blur is part of matching its material.
       Raising it here alone would break exactly the parity we want. */
    box-shadow: inset 0 0 0 1px var(--bz-p-ffda8a-a35),
                inset 0 1px 0 var(--bz-p-white-a30);
  }

  /* No hover state of its own: Android keeps :hover stuck after a tap, so any
     distinct hover colour becomes permanent. Only the background needs restating —
     the generic hover rule above touches nothing else, so the bevel survives. */
  @media (hover: hover) {
    .immersive-mode :deep(.header-button.help-button:hover) {
      background: var(--bz-p-f5c451-a70) !important;
    }
  }

  /* Pressed: the approved polarity flip, in the glass language. Ink at 0.78 rather
     than mirroring the fill's 0.70 — over a white bag, ink at 0.70 leaves the amber
     content at 3.87:1, under AA; 0.78 lifts it to 5.13:1 while staying 11.10:1 over
     black. The top highlight goes away and only the ring remains: with the light no
     longer catching the upper edge, the surface reads recessed — same reasoning as
     the flat bevel in the opaque version. The ring rises to 0.45 because it is now
     the only thing separating a dark fill from a dark tray (3.73:1 there), and it
     is #F5C451 to match the inverted content. transform: scale(0.96) still comes
     from HeaderComponent — nothing here overrides it.
     Must stay AFTER the :hover rule: same specificity, later one wins, which is
     what stops a stuck :hover from swallowing the pressed state. */
  .immersive-mode :deep(.header-button.help-button:active) {
    background: var(--bz-p-281a02-a78) !important;
    color: var(--bz-p-f5c451);
    box-shadow: inset 0 0 0 1px var(--bz-p-f5c451-a45);
  }

  /* Push orders to the bottom of the viewport */
  .immersive-mode :deep(.main-container),
  .immersive-mode :deep(.classic-layout) {
    margin-top: auto;
    z-index: var(--bz-z-order-list);
    background-color: var(--bz-surface--0c1117-a10);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  /* Order buttons — semitransparent with inner colored borders */
  .immersive-mode :deep(.order-button) {
    background: var(--bz-p-blue-400-a65) !important;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border: none !important;
    box-shadow: inset 0 0 0 2px var(--bz-p-blue-400-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  .immersive-mode :deep(.order-button.correct-order) {
    background: var(--bz-p-green-400-a65) !important;
    box-shadow: inset 0 0 0 2px var(--bz-p-green-400-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  .immersive-mode :deep(.order-button.incorrect-order) {
    background: var(--bz-p-e4a556-a65) !important;
    box-shadow: inset 0 0 0 2px var(--bz-p-e4a556-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  .immersive-mode :deep(.order-button.add-manual-order-button) {
    background: var(--bz-p-slate-600-a50) !important;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    box-shadow: inset 0 0 0 2px var(--bz-p-slate-600-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  /* Orders scroll container — remove dark border and background */
  .immersive-mode :deep(.orders-scroll-container) {
    background: transparent;
    border-top: none;
  }

  /* SelectedOrderInfoComponent — same transparency as order list */
  .immersive-mode :deep(.order-info-wrapper) {
    z-index: var(--bz-z-order-list);
    background: var(--bz-surface--0c1117-a10);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  .immersive-mode :deep(.order-id) {
    z-index: var(--bz-z-order-code);
  }

  /* Capture footer — transparent buttons + white text in immersive mode */
  .immersive-mode :deep(.back-button) {
    background: var(--bz-p-9b9b9b-a50) !important;
    border: none !important;
    color: var(--bz-p-white);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    box-shadow: inset 0 0 0 2px var(--bz-p-9b9b9b-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  .immersive-mode :deep(.camera-button) {
    background: var(--bz-p-blue-600-a65) !important;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    box-shadow: inset 0 0 0 2px var(--bz-p-blue-600-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  /* The rule above carries !important, so it also beat `.camera-button--cooldown` — which meant
     that in immersive mode (i.e. on the tablets) a locked shutter looked exactly like an
     available one, apart from the icon dimming. The operator could not tell the button was not
     listening. This restores the locked state where it is actually used; it needs !important for
     the same reason the rule above has it. Third time a stray !important has quietly disabled
     something on this button, so: if you add one here, check what it outranks. */
  .immersive-mode :deep(.camera-button--cooldown) {
    background: var(--bz-p-blue-600-a35) !important;
  }

  .immersive-mode :deep(.analyze-button) {
    background: var(--bz-p-3dd668-a50) !important;
    border: none !important;
    color: var(--bz-p-white);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    box-shadow: inset 0 0 0 2px var(--bz-p-3dd668-a80), inset 0 1px 0 var(--bz-p-white-a25), inset 0 -1px 0 var(--bz-p-white-a10);
  }

  .immersive-mode :deep(.analyze-button .photo-count) {
    color: var(--bz-p-green-700);
  }
}

/* Tablet: compact capture footer to match order list height */
@media (min-width: 768px) {
  .camera-screen :deep(.order-info-wrapper) {
    height: 132px;
    padding-bottom: 0;
    box-sizing: border-box;
  }

  .camera-screen :deep(.order-info) {
    padding: 0 32px;
  }

  .camera-screen :deep(.back-button),
  .camera-screen :deep(.camera-button),
  .camera-screen :deep(.analyze-button) {
    height: 100px;
    font-size: 24px;
    padding: 0 36px;
    border-radius: 14px;
  }

  .camera-screen :deep(.camera-button) {
    width: 100px;
    padding: 0;
  }

  .camera-screen :deep(.camera-button .pi-camera) {
    font-size: 28px;
  }

  .camera-screen :deep(.last-photo-thumb) {
    height: 80px;
  }

  .last-photo-stack {
    bottom: 23px;
    width: 110px;
  }
}

/* Immersive mode info modal */
.immersive-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--bz-p-black-a40);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: var(--bz-z-modal);
  display: flex;
  justify-content: center;
  align-items: center;
}

.immersive-modal {
  background-color: var(--bz-p-slate-900);
  color: var(--bz-p-white);
  border-radius: 16px;
  padding: 28px 24px;
  width: 340px;
  max-width: 90%;
  text-align: center;
  box-shadow: 0 8px 32px var(--bz-p-black-a30);
}

.immersive-modal-icon {
  font-size: 32px;
  color: var(--bz-p-blue-400);
  margin-bottom: 12px;
}

.immersive-modal h3 {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
}

.immersive-modal p {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--bz-p-slate-400);
}

.immersive-modal button {
  background-color: var(--bz-p-blue-500);
  color: var(--bz-p-white);
  border: none;
  border-radius: 999px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
}

/* =========================================================================
   Rider list popup — QSR at-a-glance for waiting vs. delivered riders.
   Designed to be readable at 80–100 cm from eye.
   ========================================================================= */
.rider-list-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bz-p-black-a55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: var(--bz-z-rider-list);
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 74px 16px 16px;
  box-sizing: border-box;
}

.rider-list-popup {
  background: var(--bz-surface--0f1419);
  border: 1px solid var(--bz-p-2a2f35);
  border-radius: 16px;
  padding: 18px 18px 14px;
  width: 420px;
  max-width: 100%;
  max-height: calc(100vh - 90px);
  overflow-y: auto;
  box-shadow: 0 16px 48px var(--bz-p-black-a60);
  font-family: "DM Sans", sans-serif;
}

/* The entrance slides, so it only runs for those who have not asked for less motion. */
@media (prefers-reduced-motion: no-preference) {
  .rider-list-popup {
    animation: riderPopupIn 0.22s ease-out;
  }
}

@keyframes riderPopupIn {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.rider-list-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.rider-list-popup-title {
  color: var(--bz-p-white);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.3px;
}

.rider-list-close-btn {
  background: var(--bz-p-white-a8);
  border: none;
  border-radius: 8px;
  color: var(--bz-p-gray-400);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  flex-shrink: 0;
}

@media (hover: hover) {
  .rider-list-close-btn:hover {
    background: var(--bz-p-white-a14);
    color: var(--bz-p-gray-200);
  }
}

.rider-list-section {
  margin-bottom: 14px;
}

.rider-list-section:last-child {
  margin-bottom: 0;
}

.rider-list-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 4px 0 8px;
  border-bottom: 1px solid var(--bz-p-1a1f25);
  margin-bottom: 6px;
}

.rider-list-section-header.is-waiting { color: var(--bz-p-red-500); }
.rider-list-section-header.is-delivered { color: var(--bz-p-emerald-500); }

.rider-list-section-count {
  margin-left: auto;
  background: var(--bz-p-white-a6);
  color: var(--bz-p-gray-200);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.4px;
}

.rider-list-empty {
  color: var(--bz-p-gray-500);
  font-size: 13px;
  padding: 8px 2px 4px;
}

.rider-list-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rider-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px;
  background: var(--bz-p-141a21);
  border: 1px solid var(--bz-p-1f2630);
  border-radius: 12px;
  transition: background 0.15s ease;
}

.rider-list-item.is-delivered {
  opacity: 0.82;
}

.rider-list-item-ids {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.rider-list-item-primary {
  color: var(--bz-p-white);
  font-weight: 800;
  font-size: 22px;
  line-height: 1.1;
  letter-spacing: -0.2px;
  word-break: break-all;
}

.rider-list-item-secondary {
  color: var(--bz-p-gray-400);
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  word-break: break-all;
}

.rider-list-item-platform {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  min-width: 44px;
}

.rider-list-item-platform-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
  border-radius: 6px;
  background: var(--bz-p-white-a4);
  padding: 2px;
}

.rider-list-item-platform-name {
  color: var(--bz-p-gray-300);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rider-list-item-time {
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  text-align: right;
  max-width: 140px;
  line-height: 1.2;
}

.rider-list-item-time.is-waiting.urgency-low { color: var(--bz-p-emerald-500); }
.rider-list-item-time.is-waiting.urgency-medium { color: var(--bz-p-amber-500); }
.rider-list-item-time.is-waiting.urgency-high { color: var(--bz-p-red-500); }
.rider-list-item-time.is-delivered { color: var(--bz-p-emerald-500); font-weight: 600; }

/* Tablet: bigger for 80–100 cm readability */
@media screen and (min-width: 768px) {
  .rider-list-popup {
    width: 520px;
    padding: 22px 22px 18px;
  }
  .rider-list-popup-header {
    margin-bottom: 18px;
  }
  .rider-list-popup-title {
    font-size: 26px;
  }
  .rider-list-section-header {
    font-size: 13px;
  }
  .rider-list-item-primary {
    font-size: 28px;
  }
  .rider-list-item-secondary {
    font-size: 16px;
  }
  .rider-list-item-platform-icon {
    width: 36px;
    height: 36px;
  }
  .rider-list-item-time {
    font-size: 15px;
  }
}

/* Mobile: take more width, slightly tighter */
@media screen and (max-width: 767px) {
  .rider-list-overlay {
    padding: 70px 10px 10px;
  }
  .rider-list-popup {
    width: 100%;
  }
  .rider-list-item-primary {
    font-size: 20px;
  }
}
/* ════ The service screen on the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ════
   What the camera screen lays over the live preview, on docs/sistema-diseno/forma-y-tamano.md: always a
   solid surface over the camera, never glass (norms 1 and 11); what floats wears the raised surface,
   its edge and one of the two system shadows; words never under 14 where they are read to decide; the
   palette's colours by their job. Production's rules above stay for the stores without the flag,
   immersive glass included; these win on specificity, and with `!important` exactly where
   production's immersive rules used it. */

/* The rider list: anchored under the header like a menu, as tall as its content. */
:root[data-bz-lane="system"] .rider-list-overlay {
  padding: calc(var(--bz-bar-height) + var(--bz-space-2)) var(--bz-gutter) var(--bz-gutter);
  background: var(--bz-scrim);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

:root[data-bz-lane="system"] .rider-list-popup {
  box-sizing: border-box;
  padding: var(--bz-space-4) var(--bz-space-4) var(--bz-space-5);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  box-shadow: var(--bz-shadow-menu);
  font-family: var(--bz-font);
  overscroll-behavior: contain;
}

:root[data-bz-lane="system"] .rider-list-popup-header {
  margin-bottom: var(--bz-space-2);
  padding-left: var(--bz-space-2);
}

:root[data-bz-lane="system"] .rider-list-popup-title {
  color: var(--bz-ink);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .rider-list-close-btn {
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  padding: 0;
  border-radius: var(--bz-radius-full);
  background: none;
  color: var(--bz-ink-2);
  font-size: var(--bz-icon-md);
}

:root[data-bz-lane="system"] .rider-list-section {
  margin-bottom: var(--bz-space-5);
}

:root[data-bz-lane="system"] .rider-list-section-header {
  gap: var(--bz-space-2);
  margin: 0 0 var(--bz-space-2);
  padding: 0 var(--bz-space-2);
  border: 0;
  text-transform: none;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-title);
}

:root[data-bz-lane="system"] .rider-list-section-header .bz-icon {
  font-size: var(--bz-icon-sm);
}

:root[data-bz-lane="system"] .rider-list-section-header.is-waiting { color: var(--bz-rider); }
:root[data-bz-lane="system"] .rider-list-section-header.is-delivered { color: var(--bz-ok); }

:root[data-bz-lane="system"] .rider-list-section-count {
  padding: 0 var(--bz-space-2);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .rider-list-empty {
  padding: var(--bz-space-2);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

/* The riders: one card, a hairline between them. */
:root[data-bz-lane="system"] .rider-list-items {
  gap: 0;
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
  overflow: hidden;
}

:root[data-bz-lane="system"] .rider-list-item {
  gap: var(--bz-space-3);
  padding: var(--bz-space-3) var(--bz-space-4);
  border: 0;
  border-bottom: 1px solid var(--bz-edge);
  border-radius: 0;
  background: none;
}

:root[data-bz-lane="system"] .rider-list-item:last-child {
  border-bottom: 0;
}

:root[data-bz-lane="system"] .rider-list-item.is-delivered {
  opacity: 1;
}

:root[data-bz-lane="system"] .rider-list-item-primary {
  color: var(--bz-ink);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-strong);
}

:root[data-bz-lane="system"] .rider-list-item.is-delivered .rider-list-item-primary {
  color: var(--bz-ink-2);
}

:root[data-bz-lane="system"] .rider-list-item-secondary {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  font-weight: var(--bz-weight-body);
}

:root[data-bz-lane="system"] .rider-list-item-platform-icon {
  padding: 0;
  border-radius: var(--bz-radius-sm);
  background: none;
}

:root[data-bz-lane="system"] .rider-list-item-platform-name {
  color: var(--bz-ink-2);
  text-transform: none;
  font-size: var(--bz-text-xs);
  line-height: var(--bz-leading-xs);
  letter-spacing: var(--bz-tracking-xs);
  font-weight: var(--bz-weight-body);
}

:root[data-bz-lane="system"] .rider-list-item-time {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .rider-list-item-time.is-waiting.urgency-low { color: var(--bz-ok); }
:root[data-bz-lane="system"] .rider-list-item-time.is-waiting.urgency-medium { color: var(--bz-warn); }
:root[data-bz-lane="system"] .rider-list-item-time.is-waiting.urgency-high { color: var(--bz-rider); }
:root[data-bz-lane="system"] .rider-list-item-time.is-delivered { color: var(--bz-ok); }

@media screen and (min-width: 768px) {
  :root[data-bz-lane="system"] .rider-list-popup {
    width: 520px;
  }

  :root[data-bz-lane="system"] .rider-list-item-primary {
    font-size: var(--bz-text-3xl);
    line-height: var(--bz-leading-3xl);
    letter-spacing: var(--bz-tracking-3xl);
  }
}

/* What to photograph again: a solid card over the camera, its edge in «incorrect»'s bronze orange
   (the verdict it comes from), the missing quantities in that state's luminous tag. Every product is in
   it (14-oct-2026: with seven, «y otros 4» hid which): past five and a half rows the list scrolls, the
   half row saying there is more, so the card takes touches. */
:root[data-bz-lane="system"] .retake-aim {
  display: flex;
  flex-direction: column;
  /* Never down to the bar of buttons: on the Honor with Chrome's toolbar (518 px tall) five rows and a half
     reached Atrás (14-oct-2026). The list gives up rows first. */
  max-height: calc(100vh - var(--bz-bar-height) - var(--bz-space-2) - var(--bz-target-hero) - 2 * var(--bz-gutter) - var(--bz-space-4));
  max-height: calc(100dvh - var(--bz-bar-height) - var(--bz-space-2) - var(--bz-target-hero) - 2 * var(--bz-gutter) - var(--bz-space-4));
  box-sizing: border-box;
  pointer-events: auto;
  top: calc(var(--bz-bar-height) + var(--bz-space-2));
  left: var(--bz-gutter);
  padding: var(--bz-space-3) var(--bz-space-4);
  border: 2px solid var(--bz-wrong);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  box-shadow: var(--bz-shadow-overlay);
  font-family: var(--bz-font);
}

:root[data-bz-lane="system"] .retake-aim__head {
  gap: var(--bz-space-2);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-title);
}

:root[data-bz-lane="system"] .retake-aim__items {
  flex: 0 1 auto;
  min-height: 0;
  max-height: calc(5.5 * var(--bz-target-min));
  padding-right: var(--bz-space-2);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: var(--bz-outline) transparent;
}

:root[data-bz-lane="system"] .retake-aim__item {
  min-height: var(--bz-target-min);
}

:root[data-bz-lane="system"] .retake-aim__item + .retake-aim__item,
:root[data-bz-lane="system"] .retake-aim__more {
  border-top-color: var(--bz-edge);
}

:root[data-bz-lane="system"] .retake-aim__emoji :deep(img::after) {
  background: var(--bz-raised);
}

:root[data-bz-lane="system"] .retake-aim__name {
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .retake-aim__qty {
  padding: var(--bz-space-1) var(--bz-space-3);
  border-radius: var(--bz-radius-full);
  background: var(--bz-wrong-solid);
  color: var(--bz-on-wrong-solid);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .retake-aim__more {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-body);
}

:root[data-bz-lane="system"] .retake-aim__foot {
  margin-top: var(--bz-space-1);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  font-weight: var(--bz-weight-body);
}

/* The hints over the camera, as the toasts' family (src/ui/system.css `.bz-toast`, 13-oct-2026): what floats
   — the raised surface, its edge, radius 16, the short shadow — with the icon on a 48 disc in its state's
   container, the words to the left in two levels (18/600 what happened, 16 in the second ink what to do),
   no countdown (a hint by the shutter is not a notification, 14-oct). Centred, as wide as its words up to
   480, and low: just over the bar of buttons, by the shutter, where production's compact pill sat — at
   production's 240 a card this tall read as the middle of the screen. The black-photo one asks for action:
   the warning's solid, its disc in the container. */
:root[data-bz-lane="system"] .duplicate-hint.bz-hint {
  bottom: calc(var(--bz-target-hero) + 2 * var(--bz-gutter) + var(--bz-space-4));
  width: max-content;
  max-width: min(calc(100vw - 2 * var(--bz-gutter)), 480px);
  box-sizing: border-box;
  gap: var(--bz-space-4);
  padding: var(--bz-space-3) var(--bz-space-5) var(--bz-space-3) var(--bz-space-3);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  font-family: var(--bz-font);
  white-space: normal;
  text-align: start;
}

:root[data-bz-lane="system"] .bz-hint__icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  border-radius: var(--bz-radius-full);
  font-size: var(--bz-icon-md);
}

:root[data-bz-lane="system"] .bz-hint--wrong .bz-hint__icon {
  background: var(--bz-wrong-container);
  color: var(--bz-on-wrong-container);
}

:root[data-bz-lane="system"] .bz-hint--warn .bz-hint__icon {
  background: var(--bz-warn-container);
  color: var(--bz-on-warn-container);
}

/* The warning's triangle has its weight at the base: centred by its box it reads low in the disc. Half a
   step up centres it to the eye. */
:root[data-bz-lane="system"] .bz-hint--warn .bz-hint__icon .bz-icon {
  transform: translateY(calc(var(--bz-space-1) / -2));
}

:root[data-bz-lane="system"] .bz-hint__body {
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-1);
  min-width: 0;
}

:root[data-bz-lane="system"] .bz-hint__title {
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
  overflow-wrap: anywhere;
}

:root[data-bz-lane="system"] .bz-hint__text {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
}

/* On a phone the reminder lands on the order list, which has no bar of buttons: at the bottom, inside the
   gutter, not in the middle of the cards (Adrián, 14-oct-2026). */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .duplicate-hint.bz-hint--wrong {
    bottom: calc(var(--bz-gutter) + env(safe-area-inset-bottom, 0px));
  }
}

:root[data-bz-lane="system"] .duplicate-hint.bz-hint--urgent {
  border-color: transparent;
  background: var(--bz-warn-solid);
  color: var(--bz-on-warn-solid);
}

:root[data-bz-lane="system"] .bz-hint--urgent .bz-hint__icon {
  background: var(--bz-warn-container);
  color: var(--bz-on-warn-container);
}

:root[data-bz-lane="system"] .bz-hint--urgent .bz-hint__text {
  color: inherit;
}


/* The last photo: a frame in the ink, the cards behind it on the raised surface. */
:root[data-bz-lane="system"] .last-photo-stack::before,
:root[data-bz-lane="system"] .last-photo-stack::after {
  border-color: var(--bz-edge);
  border-radius: var(--bz-radius-md);
  background: var(--bz-raised);
}

:root[data-bz-lane="system"] .last-photo-thumb {
  border-color: var(--bz-ink);
  border-radius: var(--bz-radius-md);
  box-shadow: var(--bz-shadow-overlay);
}

/* The one-time explanation of immersive mode: the system's dialog, with the main button of a 56. */
:root[data-bz-lane="system"] .immersive-modal-overlay {
  background: var(--bz-scrim);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

:root[data-bz-lane="system"] .immersive-modal {
  box-sizing: border-box;
  padding: var(--bz-space-6);
  border: 1px solid var(--bz-edge);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  font-family: var(--bz-font);
}

:root[data-bz-lane="system"] .immersive-modal {
  width: min(100%, 440px);
  border-radius: var(--bz-radius-xl);
}

:root[data-bz-lane="system"] .immersive-modal-icon {
  margin-bottom: var(--bz-space-3);
  color: var(--bz-action-ink);
  font-size: var(--bz-icon-lg);
}

:root[data-bz-lane="system"] .immersive-modal h3 {
  margin: 0 0 var(--bz-space-2);
  color: var(--bz-ink);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .immersive-modal p {
  margin: 0 0 var(--bz-space-6);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
}

:root[data-bz-lane="system"] .immersive-modal button {
  min-height: var(--bz-target-md);
  padding: 0 var(--bz-space-6);
  border-radius: var(--bz-radius-md);
  background: var(--bz-action);
  color: var(--bz-on-action);
  font-family: var(--bz-font);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

/* The RBE pilot's bar on the phone: the background, the code at the code's weight, a capsule of 48. */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .camera-screen.rbe-cam .rbe-order-bar {
    gap: var(--bz-space-3);
    padding: var(--bz-space-2) var(--bz-gutter);
    background: var(--bz-bg);
  }

  :root[data-bz-lane="system"] .rbe-order-bar-icon {
    border-radius: var(--bz-radius-md);
  }

  :root[data-bz-lane="system"] .rbe-order-bar-code {
    color: var(--bz-ink);
    font-family: var(--bz-font);
    font-size: var(--bz-text-3xl);
    line-height: var(--bz-leading-3xl);
    font-weight: var(--bz-weight-strong);
  }

  :root[data-bz-lane="system"] .rbe-ticket-toggle,
  :root[data-bz-lane="system"] .rbe-ticket-toggle--open {
    min-height: var(--bz-target-min);
    gap: var(--bz-space-1);
    padding: 0 var(--bz-space-4);
    border: 1px solid var(--bz-edge);
    border-radius: var(--bz-radius-full);
    background: var(--bz-control);
    color: var(--bz-ink);
    font-family: var(--bz-font);
    font-size: var(--bz-text-md);
    line-height: var(--bz-leading-md);
    font-weight: var(--bz-weight-label);
  }

  /* A 48 button with words: its icon at 20, as BzButton's of that size. */
  :root[data-bz-lane="system"] .rbe-ticket-toggle .bz-icon {
    font-size: var(--bz-icon-sm);
  }

  :root[data-bz-lane="system"] .rbe-ticket-toggle--open {
    border-color: var(--bz-action-ink);
    background: var(--bz-action-container);
  }
}

/* The tablet: production's compact footer (100-px buttons, radius 14) gives way to the system's 96 with
   the radius of a 96 button; the gutter on the three sides. Restated here because these rules reach the
   child components with the same weight as their own.
   The footer is the order list's bar exactly: production's 132 of height, the same hairline on top and the
   same background, with the three buttons centred in it (18 above and below, as the cards' 16 around 100).
   Out of immersive mode the preview is sized to what the header and this bar leave
   (CameraPreviewComponent's calculatePreviewHeight), so a footer of another height moved the camera when an
   order opened — 3 px, and the hairline vanished (Adrián, 14-oct-2026). */
@media (min-width: 768px) {
  :root[data-bz-lane="system"] .camera-screen :deep(.order-info-wrapper) {
    box-sizing: content-box;
    height: 132px;
    padding: 0;
    border-top: 1px solid var(--bz-edge);
    background: var(--bz-bg);
  }

  :root[data-bz-lane="system"] .camera-screen :deep(.order-info) {
    padding: 0 var(--bz-gutter);
  }

  :root[data-bz-lane="system"] .camera-screen :deep(.back-button),
  :root[data-bz-lane="system"] .camera-screen :deep(.camera-button),
  :root[data-bz-lane="system"] .camera-screen :deep(.analyze-button) {
    height: var(--bz-target-hero);
    border-radius: var(--bz-radius-xl);
    font-size: var(--bz-text-xl);
    line-height: var(--bz-leading-xl);
  }

  :root[data-bz-lane="system"] .camera-screen :deep(.back-button) {
    padding: 0 var(--bz-space-7) 0 var(--bz-space-6);
  }

  :root[data-bz-lane="system"] .camera-screen :deep(.analyze-button) {
    padding: 0 var(--bz-space-6) 0 var(--bz-space-7);
  }

  :root[data-bz-lane="system"] .camera-screen :deep(.analyze-button--shoot) {
    padding: 0 var(--bz-space-7);
  }

  :root[data-bz-lane="system"] .camera-screen :deep(.camera-button) {
    width: var(--bz-target-hero);
    padding: 0;
  }

  :root[data-bz-lane="system"] .last-photo-stack {
    bottom: calc(var(--bz-gutter) + var(--bz-space-2));
  }

  /* Immersive: the camera fills the screen, and the photo keeps the whole frame («Encuadra con toda la
     pantalla; la foto se guarda entera»). The bars carry a light flat veil (`--bz-veil`), so what sits on
     them reads as a bar and not as stickers on the picture — and the frame still shows through it; what is
     on them is solid: the buttons, the cards. No glass and no blur: a blur over the live camera is
     repainted every frame, during the capture too (Adrián asked for the veil back, 13-oct-2026). */
  :root[data-bz-lane="system"] .immersive-mode :deep(.header),
  :root[data-bz-lane="system"] .immersive-mode :deep(.main-container),
  :root[data-bz-lane="system"] .immersive-mode :deep(.classic-layout) {
    border-top: 0;
    background: var(--bz-veil);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.header-button) {
    border: 1px solid var(--bz-edge) !important;
    background: var(--bz-control) !important;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.rider-counter.alert) {
    border-color: transparent !important;
    background: var(--bz-rider-solid) !important;
    outline: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.header-button.help-button),
  :root[data-bz-lane="system"] .immersive-mode :deep(.header-button.help-button:active) {
    border-color: transparent !important;
    background: var(--bz-brand) !important;
    color: var(--bz-on-brand);
    box-shadow: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.order-button) {
    border: 0 !important;
    background: var(--bz-todo-solid) !important;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.order-button.correct-order) {
    background: var(--bz-ok-solid) !important;
    box-shadow: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.order-button.incorrect-order) {
    background: var(--bz-wrong-solid) !important;
    box-shadow: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.order-button.pending-order) {
    background: var(--bz-pending-solid) !important;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.order-button.add-manual-order-button) {
    border: 1px solid var(--bz-edge) !important;
    background: var(--bz-surface) !important;
    box-shadow: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.order-info-wrapper) {
    border-top: 0;
    background: var(--bz-veil);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.back-button) {
    border: 1px solid var(--bz-edge) !important;
    background: var(--bz-control) !important;
    color: var(--bz-ink);
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.camera-button) {
    background: var(--bz-action) !important;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.camera-button--cooldown) {
    background: var(--bz-action-container) !important;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.analyze-button) {
    border: 0 !important;
    background: var(--bz-ok-solid) !important;
    color: var(--bz-on-ok-solid);
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  :root[data-bz-lane="system"] .immersive-mode :deep(.analyze-button .photo-count) {
    color: var(--bz-on-ok-container);
  }
}
</style>